import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getToken as getAuthToken } from '../../../services/auth';
import { showLoader, dismissLoader } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import { useParams, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { Tooltip } from '@mui/material';
import {
  ArrowBack,
  AttachFile,
  Badge,
  BusinessCenter,
  CloudDownload,
  Edit,
  Email,
  InsertDriveFile,
  Cake,
  LocationOn,
  Person,
  Phone,
  Security,
  VerifiedUser,
  Visibility,
  Warning,
  Key,
  AddCircleOutline,
  RemoveCircleOutline,
  NavigateNext,
  Work,
  LocalHospital,
  DriveEta,
  Public,
} from '@mui/icons-material';
import { ROUTES } from '../../../routes';
import axios from 'axios';
import apiService from '../../../services/api';
import { API_ENDPOINTS, API_BASE_V1 } from '../../../Api/endpoints';
import { addAuthHeader } from '../../../utils/authUtils';
import { STAT_FIELDS, DOCUMENT_TYPE_MAPPING } from '../../../Configurations/StatFieldData';
import AuthenticatedImage from '../../../Components/AuthenticatedImage';
import { EMPLOYEE_STATUS_OPTIONS } from './EmployeeEdit';

import { API_PATHS } from '../../../utils/constants';
import {
  toProcessingDocumentType as toProcessingDocumentTypeShared,
  isExtractionSupported as isExtractionSupportedShared,
  buildSharedMetadataFromExtraction as buildSharedMetadataFromExtractionShared,
  buildEmployeeUpdatePayloadFromPassport as buildEmployeeUpdatePayloadFromPassportShared,
  buildEmployeeUpdatePayloadFromOhc as buildEmployeeUpdatePayloadFromOhcShared,
  buildEmployeeUpdatePayloadFromDrivingLicense as buildEmployeeUpdatePayloadFromDrivingLicenseShared,
} from '../../../utils/employeeDocumentUtils';

// Build reverse mapping: frontend (camelCase) -> backend (snake_case/UPPER) for robust matching
const REVERSE_DOCUMENT_TYPE_MAPPING = Object.fromEntries(
  Object.entries(DOCUMENT_TYPE_MAPPING || {}).map(([backendType, frontendType]) => [frontendType, backendType])
);

// Strong backend normalizer: from any label/key to backend snake_case
const normalizeBackendDocType = (input) => {
  if (!input) return input;
  const raw = String(input).trim();
  // 1) Try standard path: frontendKey -> reverse mapping
  const fk = toFrontendDocKey(raw);
  const mappedStd = REVERSE_DOCUMENT_TYPE_MAPPING[fk];
  if (mappedStd) return mappedStd; // e.g., passportCopy -> passport_copy

  // 2) Try loose match directly against mapping keys (backend) ignoring separators
  const norm = raw.toLowerCase().replace(/[_\s]/g, '');
  const kLoose = Object.keys(DOCUMENT_TYPE_MAPPING).find(k => k.toLowerCase().replace(/[_\s]/g, '') === norm && k.includes('_'));
  if (kLoose) return kLoose; // already snake_case

  // 3) Try values (frontend) then reverse map
  const vLoose = Object.values(DOCUMENT_TYPE_MAPPING).find(v => String(v).toLowerCase().replace(/[_\s]/g, '') === norm);
  if (vLoose && REVERSE_DOCUMENT_TYPE_MAPPING[vLoose]) return REVERSE_DOCUMENT_TYPE_MAPPING[vLoose];

  // 4) Fallback: camel/Pascal to snake_case lowercase
  return raw
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .toLowerCase();
};

// Helpers: robust extraction of passport number and expiry from various metadata shapes
const readPassportNumber = (doc) => {
  if (!doc) return null;
  const raw = doc._raw || doc;
  const tryKeys = (
    obj,
    keys
  ) => keys.reduce((v, k) => v ?? (obj && obj[k] != null ? obj[k] : null), null);

  // Flat common keys
  let val = tryKeys(raw, ['passportNumber', 'documentNumber', 'passport_no', 'passportNo', 'passport_number', 'passportNum']);
  if (val) return String(val);
  // Top-level doc fields
  val = tryKeys(doc, ['passportNumber', 'documentNumber']);
  if (val) return String(val);
  // Known containers
  val = tryKeys(raw.metadata || {}, ['passportNumber', 'documentNumber', 'passport_no', 'passportNo']);
  if (val) return String(val);
  val = tryKeys(raw.extracted || {}, ['passportNumber', 'documentNumber', 'passport_no', 'passportNo']);
  if (val) return String(val);
  val = tryKeys(raw.passport || {}, ['number', 'passportNumber']);
  if (val) return String(val);
  // Nested under extracted.passport
  val = tryKeys((raw.extracted && raw.extracted.passport) || {}, ['number', 'passportNumber', 'documentNumber', 'passport_no', 'passportNo']);
  if (val) return String(val);
  return null;
};

const readPassportExpiry = (doc) => {
  if (!doc) return null;
  const raw = doc._raw || doc;
  const tryKeys = (
    obj,
    keys
  ) => keys.reduce((v, k) => v ?? (obj && obj[k] != null ? obj[k] : null), null);

  const normalizeDate = (v) => (v ? v : null);

  // Prefer the mapped expiryDate on the normalized doc first
  if (doc.expiryDate) return normalizeDate(doc.expiryDate);

  // Flat common keys
  let val = tryKeys(raw, ['passportExpiry', 'expiryDate', 'expirationDate', 'expireDate', 'expiry', 'expiry_date']);
  if (val) return normalizeDate(val);
  // Known containers
  val = tryKeys(raw.metadata || {}, ['passportExpiry', 'expiryDate', 'expiry']);
  if (val) return normalizeDate(val);
  val = tryKeys(raw.extracted || {}, ['passportExpiry', 'expiryDate', 'expiry']);
  if (val) return normalizeDate(val);
  val = tryKeys(raw.passport || {}, ['expiry', 'expiryDate']);
  if (val) return normalizeDate(val);
  // Nested under extracted.passport
  val = tryKeys((raw.extracted && raw.extracted.passport) || {}, ['expiry', 'expiryDate', 'expirationDate']);
  if (val) return normalizeDate(val);
  return null;
};

// Robust detector for Passport category across label/key formats
const isPassportCategoryLabel = (label) => {
  if (!label) return false;
  const raw = String(label).trim();
  const upperUnderscore = raw.toUpperCase().replace(/\s+/g, '_');
  if (upperUnderscore === 'PASSPORT_COPY' || upperUnderscore === 'PASSPORT') return true;
  const fk = toFrontendDocKey(raw);
  if (!fk) return false;
  if (fk === 'passportCopy' || fk === 'passport') return true;
  const proc = toProcessingDocumentType(fk);
  return proc === 'PASSPORT_COPY';
};

// Utility function to convert document type between frontend and backend formats
// Backend expects lowercase snake_case (e.g., passport_copy)
const formatDocumentType = (documentType, direction = 'toBackend') => {
  if (!documentType) return documentType;

  if (direction === 'toBackend') {
    // Prefer explicit mapping from frontend (camelCase) -> backend (snake_case)
    const mapped = REVERSE_DOCUMENT_TYPE_MAPPING[documentType];
    if (mapped) return mapped;
    // Fallback: convert camelCase/PascalCase to snake_case lowercase
    return String(documentType).replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
  }

  // toFrontend: map snake_case -> camelCase when possible, else return input
  const frontend = DOCUMENT_TYPE_MAPPING[documentType];
  return frontend || documentType;
};

// Map frontend camelCase to processing enum (UPPER_SNAKE) — delegated to shared util
const toProcessingDocumentType = toProcessingDocumentTypeShared;

// Supported categories for OCR/extraction — delegated to shared util
const isExtractionSupported = isExtractionSupportedShared;

// Convert any category label or backend type to frontend camelCase key
const toFrontendDocKey = (docTypeOrLabel) => {
  if (!docTypeOrLabel) return docTypeOrLabel;
  const raw = String(docTypeOrLabel).trim();
  // 1) If input is snake_case from backend, convert to camelCase
  if (raw.includes('_')) {
    return raw.split('_').map((p, i) => i === 0 ? p : (p.charAt(0).toUpperCase() + p.slice(1))).join('');
  }
  // 2) If already a known frontend key (camelCase) present in STAT_FIELDS, keep it
  if (STAT_FIELDS[raw]) return raw;
  // 3) Match by label from STAT_FIELDS (case-insensitive)
  const labelMatch = Object.entries(STAT_FIELDS).find(([, v]) => String(v.label || '').toLowerCase() === raw.toLowerCase());
  if (labelMatch) return labelMatch[0];
  // 4) If mapping reverse says it's already a frontend key, return as-is
  if (REVERSE_DOCUMENT_TYPE_MAPPING[raw]) return raw;
  // 5) Best-effort: convert space-separated words to camelCase ("Passport Copy" -> "passportCopy")
  if (/\s/.test(raw)) {
    const parts = raw.split(/\s+/);
    return parts.map((p, i) => i === 0 ? p.charAt(0).toLowerCase() + p.slice(1) : (p.charAt(0).toUpperCase() + p.slice(1))).join('');
  }
  // 6) Loose fallback: if raw is lowercase without underscores (e.g., 'passportcopy'), try match against known keys/labels
  const loose = raw.toLowerCase();
  // check STAT_FIELDS keys
  const statKeyLoose = Object.keys(STAT_FIELDS).find(k => k.toLowerCase() === loose);
  if (statKeyLoose) return statKeyLoose;
  // check STAT_FIELDS labels (strip spaces/underscores)
  const looseNoSep = loose.replace(/[_\s]/g, '');
  const byLabelLoose = Object.entries(STAT_FIELDS).find(([, v]) => String(v.label || '').toLowerCase().replace(/[_\s]/g, '') === looseNoSep);
  if (byLabelLoose) return byLabelLoose[0];
  // check DOCUMENT_TYPE_MAPPING keys and values with loose compare
  const docKeyLoose = Object.keys(DOCUMENT_TYPE_MAPPING).find(k => k.toLowerCase().replace(/[_\s]/g, '') === looseNoSep);
  if (docKeyLoose) return DOCUMENT_TYPE_MAPPING[docKeyLoose];
  const docValLoose = Object.values(DOCUMENT_TYPE_MAPPING).find(v => String(v).toLowerCase().replace(/[_\s]/g, '') === looseNoSep);
  if (docValLoose) return docValLoose;
  return raw;
};

// (moved inside component) refreshDocuments

// Document categories based on the StatFieldData.js file
const DOCUMENT_CATEGORIES = Object.entries(STAT_FIELDS)
  .filter(([key, value]) => value.type === 'FILE')
  .map(([key, value]) => ({
    value: key,
    label: value.label
  }));

// Employee document types are now fetched from the API (/v1/document-types)
// instead of being hardcoded. See employeeDocTypeCodes inside the component.

const formatDocTypeLabelFromBackendKey = (backendKey) => {
  if (!backendKey) return '';
  const tokens = String(backendKey)
    .trim()
    .split('_')
    .filter(Boolean)
    .map((t) => t.toLowerCase());

  return tokens
    .map((t) => {
      if (t === 'id') return 'ID';
      if (t === 'ohc') return 'OHC';
      if (t === 'iloe') return 'ILOE';
      return t.charAt(0).toUpperCase() + t.slice(1);
    })
    .join(' ');
};

const formatDocTypeLabel = (docTypeOrLabel) => {
  if (!docTypeOrLabel) return '';
  const raw = String(docTypeOrLabel).trim();
  let backendKey = normalizeBackendDocType(raw);
  if (backendKey === 'driving_license') backendKey = 'driving_licence';
  if (backendKey) {
    return formatDocTypeLabelFromBackendKey(backendKey);
  }
  const frontendKey = toFrontendDocKey(raw);
  const statLabel = STAT_FIELDS?.[frontendKey]?.label;
  if (statLabel) return statLabel;
  if (raw.includes('_')) return formatDocTypeLabelFromBackendKey(raw);
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Parse a date string safely as a local date when in YYYY-MM-DD format to avoid UTC shift
const parseLocalDate = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  const ymd = /^\d{4}-\d{2}-\d{2}$/;
  if (ymd.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // Fallback: allow full ISO or other formats
  const d2 = new Date(str);
  return isNaN(d2.getTime()) ? null : d2;
};

// Format date for display, with optional timestamp
const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  const date = parseLocalDate(dateString);
  if (!date) return 'N/A';
  if (includeTime) {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Format date with time for display (kept for backward compatibility)
const formatDateTime = (dateString) => {
  return formatDate(dateString, true);
};

// Calculate days until expiry (treat YYYY-MM-DD as local date, compare by local-midnight days)
const calculateDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const exp = parseLocalDate(expiryDate);
  if (!exp) return null;
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const expStart = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate());
  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.ceil((expStart - todayStart) / msPerDay);
  return diffDays;
};

// Format file size for display
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Custom TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function EmployeeDetail({ mode = 'employee' }) {
  const isStakeholder = mode === 'stakeholder';
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  // New storage documents
  const [newStorageDocuments, setNewStorageDocuments] = useState([]);
  
  // UID update state
  const [uidDialogOpen, setUidDialogOpen] = useState(false);
  const [newUid, setNewUid] = useState('');
  const [uidError, setUidError] = useState('');
  const [uidUpdateLoading, setUidUpdateLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  // Expanded rows for category details (e.g., Passport)
  const [expandedCats, setExpandedCats] = useState({});
  
  // Document viewer modal state
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  // Category viewer modal state (View All in a category)
  const [categoryViewerOpen, setCategoryViewerOpen] = useState(false);
  const [categoryViewerTitle, setCategoryViewerTitle] = useState('Documents');
  const [categoryViewerDocs, setCategoryViewerDocs] = useState([]);

  // Replace All Dialog state
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replaceDialogCategory, setReplaceDialogCategory] = useState(null); // frontend label or backend type
  const [replaceDialogFiles, setReplaceDialogFiles] = useState([]); // File[]
  const replaceDialogInputRef = useRef(null);
  // Prevent duplicate replace submits
  const [isReplacing, setIsReplacing] = useState(false);
  // Passport update confirmation state
  const [passportUpdatePromptOpen, setPassportUpdatePromptOpen] = useState(false);
  const [passportExtractedNew, setPassportExtractedNew] = useState(null); // raw extraction
  const [passportUpdatePayload, setPassportUpdatePayload] = useState(null); // fields to update
  const [passportEditMode, setPassportEditMode] = useState({}); // { passportNumber: true/false, passportExpiry: true/false }
  
  // OHC update confirmation state
  const [ohcUpdatePromptOpen, setOhcUpdatePromptOpen] = useState(false);
  const [ohcExtractedNew, setOhcExtractedNew] = useState(null);
  const [ohcUpdatePayload, setOhcUpdatePayload] = useState(null);
  const [ohcEditMode, setOhcEditMode] = useState({});

  // Driving License update confirmation state
  const [drivingLicenseUpdatePromptOpen, setDrivingLicenseUpdatePromptOpen] = useState(false);
  const [drivingLicenseExtractedNew, setDrivingLicenseExtractedNew] = useState(null);
  const [drivingLicenseUpdatePayload, setDrivingLicenseUpdatePayload] = useState(null);
  const [drivingLicenseEditMode, setDrivingLicenseEditMode] = useState({});

  // Pending bulk save params — deferred until after confirmation dialog
  const pendingBulkSaveRef = useRef(null);

  // Throttle/Coalesce grouped fetches
  const refreshInFlightRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);
  
  // Document upload state
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    category: '',
    description: '',
    documentName: ''
  });
  const [uploadErrors, setUploadErrors] = useState({});

  // Employee document types fetched from API (replaces hardcoded EMPLOYEE_UPLOAD_DOCUMENT_TYPES)
  const [employeeDocTypes, setEmployeeDocTypes] = useState([]);
  useEffect(() => {
    const fetchEmployeeDocTypes = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_PATHS.V1}/document-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const list = await res.json();
        if (Array.isArray(list)) setEmployeeDocTypes(list);
      } catch (e) { /* ignore */ }
    };
    fetchEmployeeDocTypes();
  }, []);
  const employeeDocTypeCodes = useMemo(() => employeeDocTypes.map(dt => dt.code), [employeeDocTypes]);
  const employeeDocLabelMap = useMemo(() => {
    const map = {};
    employeeDocTypes.forEach(dt => { map[dt.code] = dt.label; });
    return map;
  }, [employeeDocTypes]);

  // Helper: sleep/delay
  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  // Helper: robust extract that prefers extract-from-path (to persist) with retry,
  // and falls back to binary extract for UI if the path isn't yet readable
  const extractDocumentDetails = async ({ uid, processingType, filePath, documentIndex = 0, fileForFallback = null, maxRetries = 5 }) => {
    let lastError = null;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const body = { uid, documentType: processingType, filePath, documentIndex };
        const ex = await apiService.api.post(
          API_ENDPOINTS.DOCUMENT_PROCESSING.EXTRACT_FROM_PATH,
          body,
          { headers: { 'Content-Type': 'application/json' } }
        );
        return ex?.data || {};
      } catch (e) {
        lastError = e;
        // If 404 or server returns 500 with a 'File not found' message, wait and retry; otherwise break
        const status = e?.response?.status;
        const msg = e?.response?.data?.message || e?.response?.data || e?.message || '';
        const isRetryable = status === 404 || (status === 500 && typeof msg === 'string' && msg.toLowerCase().includes('file not found'));
        if (!isRetryable) break;
        // Exponential backoff up to ~2s
        const delay = 400 * Math.pow(1.5, attempt);
        await sleep(delay);
      }
    }
    // Fallback: try binary extract for immediate UI feedback (does not persist)
    if (fileForFallback) {
      try {
        const fd = new FormData();
        fd.append('file', fileForFallback);
        fd.append('documentType', processingType);
        fd.append('documentIndex', String(documentIndex));
        const ex = await apiService.api.post(
          API_ENDPOINTS.DOCUMENT_PROCESSING.EXTRACT,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return ex?.data || {};
      } catch (e2) {
        console.error('Extraction fallback (binary) failed:', e2);
      }
    }
    throw lastError || new Error('Extraction failed');
  };

  // Clear selected files in upload dialog
  const handleClearSelectedFiles = () => {
    setUploadData({ ...uploadData, file: null, files: [] });
  };

  // Safely derive status label even while employee data is loading
  const statusValue = employee?.status || '';
  const statusLabel =
    EMPLOYEE_STATUS_OPTIONS.find((option) => option.value === statusValue)?.label ||
    statusValue ||
    'Unknown';

  // Helper: build backend employee update payloads — delegated to shared utils
  const buildEmployeeUpdatePayloadFromPassport = buildEmployeeUpdatePayloadFromPassportShared;
  const buildEmployeeUpdatePayloadFromOhc = buildEmployeeUpdatePayloadFromOhcShared;
  const buildEmployeeUpdatePayloadFromDrivingLicense = buildEmployeeUpdatePayloadFromDrivingLicenseShared;

  // Helper: poll grouped docs until passport_copy group's common matches expected docNo/expiry
  const pollGroupedPassportUpdated = async ({ uid, expectedDocNo, expectedExpiry, maxAttempts = 12 }) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const groupedUrl = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.GROUPED_BY_UID(uid);
        const gresp = await axios.get(groupedUrl, { headers: addAuthHeader() });
        const root = gresp?.data?.data || gresp?.data || {};
        const groups = root.groups || [];
        const pgroup = groups.find(g => String(g?.common?.documentType || '').toLowerCase() === 'passport_copy');
        if (pgroup && pgroup.common) {
          const c = pgroup.common;
          const docOk = expectedDocNo ? String(c.documentNumber || '') === String(expectedDocNo) : true;
          const expOk = expectedExpiry ? String(c.expiryDate || '') === String(expectedExpiry) : true;
          if (docOk && expOk) return true;
        }
      } catch (e) {
        // ignore and retry
      }
      await sleep(500 * Math.pow(1.4, attempt));
    }
    return false;
  };

  // Helper: resolve final filePath by polling grouped documents until the uploaded file appears with its final path
  const resolveFinalFilePath = async ({ uid, processingType, hintFileName, initialPath, maxAttempts = 5 }) => {
    const categoryKey = processingType.toLowerCase();
    let lastPath = initialPath;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const groupedUrl = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.GROUPED_BY_UID(uid);
        const gresp = await axios.get(groupedUrl, { headers: addAuthHeader() });
        const groot = gresp?.data?.data || gresp?.data || {};
        const groups = groot.groups || groot || [];
        const cat = Array.isArray(groups) ? groups.find(g => (g.category || g.documentType || '').toLowerCase() === categoryKey) : null;
        const files = cat?.files || cat?.documents || [];
        if (Array.isArray(files) && files.length) {
          // Try to match by fileName first; fallback to latest created
          let match = files.find(f => (f.fileName || '').toLowerCase() === (hintFileName || '').toLowerCase());
          if (!match) {
            match = files.slice().sort((a,b) => new Date(b.createdAt||0) - new Date(a.createdAt||0))[0];
          }
          if (match?.filePath) {
            return match.filePath;
          }
        }
      } catch (e) {
        // ignore and retry
      }
      // backoff 400ms, 600ms, 900ms, 1350ms, ...
      await sleep(400 * Math.pow(1.5, attempt));
    }
    return lastPath;
  };

  // Refresh documents list from server (now in component scope)
  const refreshDocuments = async (opts = {}) => {
    const { force = false } = opts;
    if (!employee?.uid) return;
    const now = Date.now();
    if (!force) {
      if (refreshInFlightRef.current) return; // coalesce
      if (now - lastRefreshTimeRef.current < 1500) return; // throttle 1.5s
    }
    refreshInFlightRef.current = true;
    try {
      // Try grouped endpoint first
      const groupedUrl = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.GROUPED_BY_UID(employee.uid);
      console.log('Fetching grouped employee documents from:', groupedUrl);
      const gresp = await axios.get(groupedUrl, { headers: addAuthHeader() });
      // Support both {groups: [...]} and {success, data: {groups: [...]}}
      const gdataRoot = gresp?.data || {};
      const gdata = gdataRoot?.data && typeof gdataRoot.data === 'object' ? gdataRoot.data : gdataRoot;
      let groups = Array.isArray(gdata?.groups)
        ? gdata.groups
        : (Array.isArray(gdataRoot?.groups) ? gdataRoot.groups : []);
      // Include all groups for each documentType to show all documents (current and historical)
      // This ensures multiple uploads per category (e.g., visa_page) are visible together.
      if (Array.isArray(groups) && groups.length > 0) {
        const byType = new Map();
        for (const g of groups) {
          const tRaw = (g?.common?.documentType || g?.documentType || '').trim();
          const tKey = normalizeBackendDocType(tRaw);
          if (!tKey) continue;
          if (!byType.has(tKey)) byType.set(tKey, []);
          byType.get(tKey).push(g);
        }
        const selected = [];
        for (const [, arr] of byType.entries()) {
          selected.push(...arr); // push all groups for the type
        }
        groups = selected;
      }

      if (groups.length > 0) {
        // Flatten ALL groups -> UI documents, hoisting common fields onto each item
        // We intentionally include both current and historical groups so multiple
        // documents (e.g., multiple passport copies) are all visible in the UI.
        const selectedGroups = groups;
        const flattened = (selectedGroups || []).flatMap((g) => {
          const common = g.common || {};
          const docs = Array.isArray(g.documents) ? g.documents : [];
          return docs.map((it) => {
            const backendType = common.documentType || it.documentType;
            const fileName = it.fileName || 'document';
            const meta = undefined; // metadata not present in grouped items now
            const created = undefined; // not present in grouped items now
            const updated = undefined; // not present in grouped items now
            // Normalize to UI model used across the screen
            return {
              id: it.id,
              // Group-level id of this version's upload batch.
              documentGroupId: g.documentGroupId || common.documentGroupId,
              // Certificate name (multiple-instance types) + its version number.
              title: common.title || undefined,
              version: (typeof it.version === 'number') ? it.version : (it.version ? parseInt(it.version, 10) : undefined),
              uid: employee.uid,
              documentType: backendType,
              fileName,
              filePath: it.filePath,
              contentType: it.contentType,
              size: it.fileSize,
              // Inline base64 thumbnail for photos — lets the profile-photo
              // section render the avatar without an S3 fetch.
              photoThumbnailBase64: it.photoThumbnailBase64,
              expiryDate: common.expiryDate || undefined,
              createdAt: created,
              updatedAt: updated,
              active: typeof it.active === 'boolean' ? it.active : false,
              isCurrent: typeof it.isCurrent === 'boolean' ? it.isCurrent : (typeof common.isCurrent === 'boolean' ? common.isCurrent : undefined),
              isPrimary: typeof it.isPrimary === 'boolean' ? it.isPrimary : undefined,
              status: (() => {
                const exp = common.expiryDate;
                if (!exp) return 'Valid';
                const days = calculateDaysUntilExpiry(exp);
                if (days < 0) return 'Expired';
                if (days <= 180) return 'Expiring Soon';
                return 'Valid';
              })(),
              documentNumber: common.documentNumber || undefined,
              ticketId: it.ticketId || undefined,
              ticket: it.ticket || undefined,
              metadata: meta,
              _raw: { ...it, common },
            };
          });
        });
        setNewStorageDocuments(flattened);
        return;
      }
      // Fallback to flat endpoint
      const flatUrl = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.LIST_BY_UID(employee.uid);
      console.log('Grouped empty; fetching flat employee documents from:', flatUrl);
      const resp = await axios.get(flatUrl, { headers: addAuthHeader() });
      const listRaw = Array.isArray(resp?.data?.data) ? resp.data.data : (Array.isArray(resp?.data) ? resp.data : []);
      // Filter only current if available
      const listCurrent = (listRaw || []).filter(d => [d.isCurrent, d.current, d.is_current].some(v => v === true));
      if (listCurrent.length > 0) {
        setNewStorageDocuments(listCurrent);
      } else {
        // Derive current set: pick only docs that belong to the highest version per documentType
        const byType = {};
        for (const d of (listRaw || [])) {
          const rawType = d.documentType || d.type || d.category || 'unknown';
          const t = (rawType || '').toString().toLowerCase();
          const v = typeof d.version === 'number' ? d.version : parseInt(d.version, 10);
          if (!byType[t]) byType[t] = { max: Number.NEGATIVE_INFINITY };
          if (!Number.isNaN(v)) byType[t].max = Math.max(byType[t].max, v);
        }
        const derived = (listRaw || []).filter(d => {
          const rawType = d.documentType || d.type || d.category || 'unknown';
          const t = (rawType || '').toString().toLowerCase();
          const v = typeof d.version === 'number' ? d.version : parseInt(d.version, 10);
          const maxV = byType[t]?.max;
          // If version is not present anywhere for this type, keep all (fallback)
          if (maxV === Number.NEGATIVE_INFINITY || maxV == null) return true;
          return v === maxV;
        });
        setNewStorageDocuments(derived);
      }
    } catch (err) {
      console.error('Failed to refresh employee documents:', err);
      // Attempt flat endpoint as last resort
      try {
        const flatUrl = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.LIST_BY_UID(employee.uid);
        const resp = await axios.get(flatUrl, { headers: addAuthHeader() });
        const listRaw = Array.isArray(resp?.data?.data) ? resp.data.data : (Array.isArray(resp?.data) ? resp.data : []);
        const listCurrent = (listRaw || []).filter(d => [d.isCurrent, d.current, d.is_current].some(v => v === true));
        if (listCurrent.length > 0) {
          setNewStorageDocuments(listCurrent);
        } else {
          const byType = {};
          for (const d of (listRaw || [])) {
            const rawType = d.documentType || d.type || d.category || 'unknown';
            const t = (rawType || '').toString().toLowerCase();
            const v = typeof d.version === 'number' ? d.version : parseInt(d.version, 10);
            if (!byType[t]) byType[t] = { max: Number.NEGATIVE_INFINITY };
            if (!Number.isNaN(v)) byType[t].max = Math.max(byType[t].max, v);
          }
          const derived = (listRaw || []).filter(d => {
            const rawType = d.documentType || d.type || d.category || 'unknown';
            const t = (rawType || '').toString().toLowerCase();
            const v = typeof d.version === 'number' ? d.version : parseInt(d.version, 10);
            const maxV = byType[t]?.max;
            if (maxV === Number.NEGATIVE_INFINITY || maxV == null) return true;
            return v === maxV;
          });
          setNewStorageDocuments(derived);
        }
      } catch (e2) {
        console.error('Flat fallback failed:', e2);
      }
    } finally {
      refreshInFlightRef.current = false;
      lastRefreshTimeRef.current = Date.now();
    }
  };

  // Derive profile photo from canonical employee-documents list. Prefer the
  // inline base64 thumbnail (photoThumbnailBase64) — renders instantly with
  // no S3 fetch. Falls back to the full-size /v1/files/download URL only
  // when the row predates the inline-thumb feature.
  const profilePhotoDoc = Array.isArray(newStorageDocuments)
    ? newStorageDocuments.find((d) => {
        const t = (d.documentType || '').toUpperCase();
        return (d.photoThumbnailBase64 || d.filePath) && (t === 'PROFILE_PHOTO' || t === 'PHOTO' || t === 'EMPLOYEE_PHOTO');
      })
    : null;
  const profilePhotoThumb = profilePhotoDoc?.photoThumbnailBase64 || null;
  const profilePhotoUrl = !profilePhotoThumb && profilePhotoDoc?.filePath
    ? `/v1/files/download?filePath=${encodeURIComponent(profilePhotoDoc.filePath)}`
    : null;

// Document types that can hold multiple independent sub-files per employee
// (educational / experience certificates). For these the upload option stays
// available even when some already exist (so more can be added), and each
// sub-file can be replaced individually.
const MULTI_INSTANCE_DOC_TYPES = ['educational_certificate', 'experience_certificate'];

// Derive uploadable categories: single types only when not already present;
// multiple-instance types are always offered so more sub-files can be added.
const availableUploadCategories = (() => {
  const existing = new Set(
    (Array.isArray(newStorageDocuments) ? newStorageDocuments : [])
      .map((d) => normalizeBackendDocType(d.documentType || d.type || d.category))
  );
  const all = employeeDocTypeCodes;
  return all.filter((k) => MULTI_INSTANCE_DOC_TYPES.includes(k) || !existing.has(k));
})();

// Build unique upload category options (dedupe by frontend value)
const uploadCategoryOptions = React.useMemo(() => {
  const seen = new Set();
  const opts = [];
  for (const backendKey of availableUploadCategories) {
    const value = backendKey;
    const statKey = toFrontendDocKey(backendKey);
    const label = employeeDocLabelMap[backendKey] || STAT_FIELDS?.[statKey]?.label || formatDocTypeLabelFromBackendKey(backendKey);
    if (!value) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    opts.push({ value, label, backendKey });
  }
  return opts;
}, [availableUploadCategories]);

// Auto-reset invalid category selection to avoid out-of-range Select warnings
useEffect(() => {
  try {
    const current = uploadData?.category || '';
    const valid = uploadCategoryOptions.some(o => o.value === current);
    if (current && !valid) {
      setUploadData((prev) => ({ ...prev, category: '' }));
    }
  } catch (e) {
    // ignore
  }
}, [uploadCategoryOptions, uploadData?.category]);

  // Helper: return the first non-empty value from an object for the given keys
  const pickFirst = (obj, keys) => {
    if (!obj || typeof obj !== 'object' || !Array.isArray(keys)) return undefined;
    for (const k of keys) {
      const v = obj[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return undefined;
  };

  // Helper: map category to corresponding metadata keys and labels
  const readCategoryMetadata = (catKey, groupCommon, primaryItem, employeeProfile) => {
    const k = (catKey || '').toLowerCase();
    const gc = groupCommon || {};
    const pi = primaryItem || {};
    const ep = employeeProfile || {};

    const generic = {
      numberLabel: 'Document Number',
      numberValue: pickFirst(gc, ['documentNumber', 'docNumber', 'number']) || pickFirst(pi, ['documentNumber', 'docNumber', 'number']),
      expiryLabel: 'Expiry Date',
      expiryValue: pickFirst(gc, ['expiryDate', 'expiry']) || pickFirst(pi, ['expiryDate', 'expiry']),
      issueLabel: 'Issue Date',
      issueValue: pickFirst(gc, ['issueDate']) || pickFirst(pi, ['issueDate'])
    };

    if (k === 'emirates_id' || k === 'emiratesid' || k === 'eid' || k === 'emirates_id_copy') {
      return {
        numberLabel: 'Emirates ID Number',
        numberValue: pickFirst(gc, ['emiratesIdNumber','emirates_id_number','emiratesIdNo','idNumber','cardNumber','documentNumber'])
          || pickFirst(pi, ['documentNumber','idNumber','cardNumber'])
          || ep.emiratesId,
        expiryLabel: 'Emirates ID Expiry',
        expiryValue: pickFirst(gc, ['emiratesIdExpiry','emirates_id_expiry','expiryDate'])
          || pickFirst(pi, ['expiryDate'])
          || ep.emiratesIdExpiryDate,
        issueLabel: 'Issue Date',
        issueValue: pickFirst(gc, ['issueDate']) || pickFirst(pi, ['issueDate']) || ep.emiratesIdIssueDate
      };
    }

    if (k === 'visa_page' || k === 'residence_visa' || k === 'visa' || k === 'uae_visa') {
      return {
        numberLabel: 'Visa Number',
        numberValue: pickFirst(gc, ['visaNumber','visa_no','visaNo','documentNumber'])
          || pickFirst(pi, ['documentNumber'])
          || ep.visaNumber,
        expiryLabel: 'Visa Expiry',
        expiryValue: pickFirst(gc, ['visaExpiry','visa_expiry','expiryDate'])
          || pickFirst(pi, ['expiryDate'])
          || ep.visaExpiryDate,
        issueLabel: 'Issue Date',
        issueValue: pickFirst(gc, ['issueDate']) || pickFirst(pi, ['issueDate']) || ep.visaIssueDate
      };
    }

    if (k === 'labour_card' || k === 'labour_contract' || k === 'work_permit') {
      return {
        numberLabel: 'Card/Permit Number',
        numberValue: pickFirst(gc, ['cardNumber','permitNumber','documentNumber']) || pickFirst(pi, ['documentNumber']),
        expiryLabel: 'Expiry Date',
        expiryValue: pickFirst(gc, ['cardExpiry','permitExpiry','expiryDate']) || pickFirst(pi, ['expiryDate']),
        issueLabel: 'Issue Date',
        issueValue: pickFirst(gc, ['issueDate']) || pickFirst(pi, ['issueDate'])
      };
    }

    if (k === 'police_clearance_certificate') {
      return {
        numberLabel: 'Reference Number',
        numberValue: pickFirst(gc, ['documentNumber', 'referenceNumber']) || pickFirst(pi, ['documentNumber', 'referenceNumber']),
        expiryLabel: 'Expiry Date',
        expiryValue: pickFirst(gc, ['expiryDate', 'expiry']) || pickFirst(pi, ['expiryDate', 'expiry']),
        issueLabel: 'Issue Date',
        issueValue: pickFirst(gc, ['issueDate']) || pickFirst(pi, ['issueDate']),
        issuingAuthorityLabel: 'Issuing Authority',
        issuingAuthorityValue: pickFirst(gc, ['issuingAuthority', 'issuing_authority']) || pickFirst(pi, ['issuingAuthority', 'issuing_authority'])
      };
    }

    // Default generic mapping
    return generic;
  };

  // Derive active passport document globally for header/personal info display
  const activePassportDoc = (() => {
    if (!Array.isArray(newStorageDocuments) || newStorageDocuments.length === 0) return null;
    const passportDocs = newStorageDocuments.filter(d => normalizeBackendDocType(d.documentType || d.type || d.category) === 'passport_copy');
    if (passportDocs.length === 0) return null;
    const ts = (d) => (
      d.updatedAt || d.lastUpdatedAt || d.modifiedAt || d.updatedDate || d.uploadDate || d.createdAt || d.createDateTime || 0
    );
    const byDateDesc = (a, b) => new Date(ts(b)) - new Date(ts(a));
    const actives = passportDocs.filter(p => p.active === true).sort(byDateDesc);
    return actives[0] || passportDocs.slice().sort(byDateDesc)[0] || null;
  })();

  // Only show values from the active passport document using robust readers
  const displayPassportNumber = readPassportNumber(activePassportDoc);
  const displayPassportExpiry = readPassportExpiry(activePassportDoc);

  // Fetch employee data function - extracted to be reusable
  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      // Get auth token from local storage (try multiple possible keys)
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('jwt_token') || 
                    localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.GET(id), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.success && response.data.data) {
        // Convert document types from backend format (snake_case) to frontend format (camelCase)
        const employeeData = response.data.data;
        
        if (employeeData.documents && employeeData.documents.length > 0) {
          console.log('Original documents from backend:', JSON.stringify(employeeData.documents));
          
          employeeData.documents = employeeData.documents.map(doc => {
            // Convert document type using the reverse mapping
            const frontendDocType = DOCUMENT_TYPE_MAPPING[doc.documentType] || doc.documentType;
            console.log(`Converting document type: ${doc.documentType} -> ${frontendDocType}`);
            
            return {
              ...doc,
              documentType: frontendDocType
            };
          });
          
          console.log('Converted documents for frontend:', JSON.stringify(employeeData.documents));
        }
        
        setEmployee(employeeData);
        // Initialize newUid with current UID if available
        if (employeeData.uid) {
          setNewUid(employeeData.uid);
        }
      } else {
        throw new Error(response.data?.message || 'Failed to fetch employee data');
      }
    } catch (err) {
      console.error('Failed to fetch employee details:', err);
      setError(err.message || 'Failed to fetch employee details');
    } finally {
      setLoading(false);
    }
  };

  // Call fetchEmployeeData on component mount
  useEffect(() => {
    fetchEmployeeData();
  }, [id]);

  // Fetch documents after employee is loaded using grouped-aware refresh
  useEffect(() => {
    if (!employee || !employee.uid) return;
    refreshDocuments();
  }, [employee]);


  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // View document handler (extracted from inline button)
  const handleViewDocument = async (doc) => {
    try {
      // Get authentication token
      let token = localStorage.getItem('token') || localStorage.getItem('jwt_token') || localStorage.getItem('authToken');
      if (!token) {
        setSnackbarMessage('Authentication token not found. Please log in again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      if (!token.startsWith('Bearer ')) token = `Bearer ${token}`;

      const isNewStorage = !!doc.filePath;
      const employeeUid = doc.uid || employee?.uid;
      const docType = formatDocumentType(doc.documentType || doc.type, 'toBackend');
      const viewUrl = isNewStorage
        ? API_ENDPOINTS.FILES.DOWNLOAD(doc.filePath)
        : (employeeUid && docType
            ? `${API_BASE_V1}/users/${employeeUid}/documents/${docType}/download`
            : null);

      if (!viewUrl) {
        setSnackbarMessage('Document information is incomplete.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      setSnackbarMessage('Retrieving document...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);

      const response = await fetch(viewUrl, {
        method: 'GET',
        headers: addAuthHeader()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const contentType = response.headers.get('content-type') || '';

      // Open the fetched (authenticated) document directly in a new tab once the
      // API has completed — no intermediate "open securely" dialog.
      const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        // Popup was blocked — fall back to the in-app viewer dialog.
        setDocumentUrl(blobUrl);
        setDocumentTitle(formatDocTypeLabel(doc.documentType || doc.type || doc.category));
        setDocumentType(contentType);
        setDocumentViewerOpen(true);
      }

      setSnackbarMessage('Document loaded successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('VIEW - Error fetching document:', err);
      setSnackbarMessage('Failed to open document. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Confirm applying extracted passport updates to profile
  const applyPassportUpdates = async () => {
    if (!passportUpdatePayload) { setPassportUpdatePromptOpen(false); return; }
    try {
      // Execute deferred bulk save
      const pending = pendingBulkSaveRef.current;
      if (pending) {
        pendingBulkSaveRef.current = null;
        await bulkSaveEmployeeDocuments(pending);
      }
      const payload = buildEmployeeUpdatePayloadFromPassport(passportUpdatePayload);
      console.debug('applyPassportUpdates: sending payload', payload);
      await axios.put(
        API_ENDPOINTS.EMPLOYEES.UPDATE(id),
        payload,
        { headers: addAuthHeader({ 'Content-Type': 'application/json' }) }
      );
      // Update local state for quick feedback as well
      setEmployee(prev => ({ ...prev, ...passportUpdatePayload }));
      // Determine correct UID for grouped polling
      let uid = employee?.uid;
      if (!uid) {
        try {
          const eres = await axios.get(API_ENDPOINTS.EMPLOYEES.DETAILS(id), { headers: addAuthHeader() });
          uid = eres?.data?.data?.uid || eres?.data?.uid;
        } catch (e) {
          console.warn('Failed to resolve UID from employee details; grouped poll may fail');
        }
      }
      // Small delay to allow backend to finalize cloning/persistence
      await sleep(600);
      // Poll grouped endpoint to ensure backend created a new current passport_copy group with updated common
      if (uid) {
        await pollGroupedPassportUpdated({
          uid,
          expectedDocNo: passportUpdatePayload.passportNumber,
          expectedExpiry: passportUpdatePayload.passportExpiry,
          maxAttempts: 8
        });
      }
      await refreshDocuments({ force: true });
      setSnackbarMessage('Passport information updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Passport update failed:', e);
      setSnackbarMessage('Failed to update passport info');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setPassportUpdatePromptOpen(false);
      setPassportExtractedNew(null);
      setPassportUpdatePayload(null);
      setPassportEditMode({});
      setReplaceDialogOpen(false);
      setReplaceDialogFiles([]);
      setReplaceDialogCategory(null);
    }
  };

  const cancelPassportUpdates = async () => {
    // Execute deferred bulk save with current employee values as sharedMetadata
    const pending = pendingBulkSaveRef.current;
    if (pending) {
      pendingBulkSaveRef.current = null;
      const currentPassportNumber = employee.passportNumber || displayPassportNumber || null;
      const currentPassportExpiry = employee.passportExpiry || displayPassportExpiry || null;
      const currentMetadata = {};
      if (currentPassportNumber) currentMetadata.documentNumber = currentPassportNumber;
      if (currentPassportExpiry) currentMetadata.expiryDate = currentPassportExpiry;
      await bulkSaveEmployeeDocuments({ ...pending, sharedMetadata: Object.keys(currentMetadata).length ? currentMetadata : undefined });
    }
    setPassportUpdatePromptOpen(false);
    setPassportExtractedNew(null);
    setPassportUpdatePayload(null);
    setPassportEditMode({});
    setReplaceDialogOpen(false);
    setReplaceDialogFiles([]);
    setReplaceDialogCategory(null);
    await refreshDocuments({ force: true });
  };

  // Confirm applying extracted OHC updates to profile
  const applyOhcUpdates = async () => {
    if (!ohcUpdatePayload) { setOhcUpdatePromptOpen(false); return; }
    try {
      // Execute deferred bulk save
      const pending = pendingBulkSaveRef.current;
      if (pending) {
        pendingBulkSaveRef.current = null;
        await bulkSaveEmployeeDocuments(pending);
      }
      const payload = buildEmployeeUpdatePayloadFromOhc(ohcUpdatePayload);
      console.debug('applyOhcUpdates: sending payload', payload);
      await axios.put(
        API_ENDPOINTS.EMPLOYEES.UPDATE(id),
        payload,
        { headers: addAuthHeader({ 'Content-Type': 'application/json' }) }
      );
      setEmployee(prev => ({ ...prev, ...ohcUpdatePayload }));
      await sleep(600);
      await refreshDocuments({ force: true });
      setSnackbarMessage('OHC information updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('OHC update failed:', e);
      setSnackbarMessage('Failed to update OHC info');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setOhcUpdatePromptOpen(false);
      setOhcExtractedNew(null);
      setOhcUpdatePayload(null);
      setOhcEditMode({});
      setReplaceDialogOpen(false);
      setReplaceDialogFiles([]);
      setReplaceDialogCategory(null);
    }
  };

  const cancelOhcUpdates = async () => {
    // Execute deferred bulk save with current employee values as sharedMetadata
    const pending = pendingBulkSaveRef.current;
    if (pending) {
      pendingBulkSaveRef.current = null;
      const currentMetadata = {};
      if (employee.ohcIssueDate) currentMetadata.issueDate = employee.ohcIssueDate;
      if (employee.ohcExpiryDate) currentMetadata.expiryDate = employee.ohcExpiryDate;
      await bulkSaveEmployeeDocuments({ ...pending, sharedMetadata: Object.keys(currentMetadata).length ? currentMetadata : undefined });
    }
    setOhcUpdatePromptOpen(false);
    setOhcExtractedNew(null);
    setOhcUpdatePayload(null);
    setOhcEditMode({});
    setReplaceDialogOpen(false);
    setReplaceDialogFiles([]);
    setReplaceDialogCategory(null);
    await refreshDocuments({ force: true });
  };

  // Confirm applying extracted Driving License updates to profile
  const applyDrivingLicenseUpdates = async () => {
    if (!drivingLicenseUpdatePayload) { setDrivingLicenseUpdatePromptOpen(false); return; }
    try {
      // Execute deferred bulk save
      const pending = pendingBulkSaveRef.current;
      if (pending) {
        pendingBulkSaveRef.current = null;
        await bulkSaveEmployeeDocuments(pending);
      }
      const payload = buildEmployeeUpdatePayloadFromDrivingLicense(drivingLicenseUpdatePayload);
      console.debug('applyDrivingLicenseUpdates: sending payload', payload);
      await axios.put(
        API_ENDPOINTS.EMPLOYEES.UPDATE(id),
        payload,
        { headers: addAuthHeader({ 'Content-Type': 'application/json' }) }
      );
      setEmployee(prev => ({ ...prev, ...drivingLicenseUpdatePayload }));
      await sleep(600);
      await refreshDocuments({ force: true });
      setSnackbarMessage('Driving License information updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Driving License update failed:', e);
      setSnackbarMessage('Failed to update Driving License info');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDrivingLicenseUpdatePromptOpen(false);
      setDrivingLicenseExtractedNew(null);
      setDrivingLicenseUpdatePayload(null);
      setDrivingLicenseEditMode({});
      setReplaceDialogOpen(false);
      setReplaceDialogFiles([]);
      setReplaceDialogCategory(null);
    }
  };

  const cancelDrivingLicenseUpdates = async () => {
    // Execute deferred bulk save with current employee values as sharedMetadata
    const pending = pendingBulkSaveRef.current;
    if (pending) {
      pendingBulkSaveRef.current = null;
      const currentMetadata = {};
      if (employee.drivingLicenseIssueDate) currentMetadata.issueDate = employee.drivingLicenseIssueDate;
      if (employee.drivingLicenseExpiryDate) currentMetadata.expiryDate = employee.drivingLicenseExpiryDate;
      if (employee.drivingLicensePlaceOfIssue) currentMetadata.placeOfIssue = employee.drivingLicensePlaceOfIssue;
      await bulkSaveEmployeeDocuments({ ...pending, sharedMetadata: Object.keys(currentMetadata).length ? currentMetadata : undefined });
    }
    setDrivingLicenseUpdatePromptOpen(false);
    setDrivingLicenseExtractedNew(null);
    setDrivingLicenseUpdatePayload(null);
    setDrivingLicenseEditMode({});
    setReplaceDialogOpen(false);
    setReplaceDialogFiles([]);
    setReplaceDialogCategory(null);
    await refreshDocuments({ force: true });
  };

  // Download document handler (extracted from inline button)
  const handleDownloadDocument = async (doc) => {
    const isNewStorage = !!doc.filePath;
    try {
      let token = localStorage.getItem('token') || localStorage.getItem('jwt_token') || localStorage.getItem('authToken');
      if (token) {
        if (!token.startsWith('Bearer ')) {
          token = `Bearer ${token}`;
        }
      } else {
        setSnackbarMessage('Authentication token not found. Please log in again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      setSnackbarMessage('Downloading document...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);

      let downloadUrl;
      const rawDocumentType = doc.documentType || doc.type || '';
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(rawDocumentType);
      const isDerivedOrNonStandard = /minified/i.test(rawDocumentType) || /thumbnail/i.test(rawDocumentType) || isUuid;
      const currentUid = employee?.uid || doc.uid;

      let preferredDoc = doc.filePath
        ? doc
        : (Array.isArray(newStorageDocuments)
            ? newStorageDocuments.find(d => {
                const dType = d.documentType || '';
                const dUid = d.uid;
                const matchesDirect = dType.toLowerCase() === rawDocumentType.toLowerCase();
                const mappedToFrontend = (DOCUMENT_TYPE_MAPPING && DOCUMENT_TYPE_MAPPING[dType]) ? DOCUMENT_TYPE_MAPPING[dType] : null;
                const matchesViaMapping = mappedToFrontend ? (mappedToFrontend === rawDocumentType) : false;
                const reverseBackend = (REVERSE_DOCUMENT_TYPE_MAPPING && REVERSE_DOCUMENT_TYPE_MAPPING[rawDocumentType]) ? REVERSE_DOCUMENT_TYPE_MAPPING[rawDocumentType] : null;
                const matchesReverse = reverseBackend ? (dType.toLowerCase() === reverseBackend.toLowerCase()) : false;
                return (matchesDirect || matchesViaMapping || matchesReverse) && (dUid === currentUid) && !!d.filePath;
              })
            : null);
      if (!preferredDoc && Array.isArray(newStorageDocuments) && (doc.fileName || doc.name)) {
        const targetName = (doc.fileName || doc.name).toLowerCase();
        preferredDoc = newStorageDocuments.find(d => (d.uid === currentUid) && !!d.filePath && (d.fileName || '').toLowerCase() === targetName);
      }

      if (preferredDoc && preferredDoc.filePath) {
        downloadUrl = API_ENDPOINTS.FILES.DOWNLOAD(preferredDoc.filePath);
      } else if (isDerivedOrNonStandard) {
        setSnackbarMessage('File path missing for this document. Cannot download.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      } else {
        const uid = currentUid;
        const documentType = rawDocumentType;
        if (!uid || !documentType) {
          setSnackbarMessage('Document information is incomplete. Missing UID or document type.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }
        downloadUrl = `${API_BASE_V1}/users/${uid}/documents/${documentType}/download`;
      }

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: addAuthHeader()
      });

      if (!response.ok) {
        throw new Error(`Failed to download document. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = doc.fileName || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbarMessage('Document downloaded successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('DOWNLOAD - Error downloading document:', error);
      setSnackbarMessage(`Failed to download document: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };
 
  // Refs for category replace
  const replaceCategoryInputRef = useRef(null);
  const replaceCategoryTargetRef = useRef(null); // { backendDocType, categoryLabel }

  // Download all documents in a category (sequential, auth-aware)
  const handleDownloadCategory = async (catDocs, catName) => {
    if (!Array.isArray(catDocs) || catDocs.length === 0) return;
    try {
      setSnackbarMessage(`Downloading ${catDocs.length} file(s) for ${catName}...`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      for (const d of catDocs) {
        // eslint-disable-next-line no-await-in-loop
        await handleDownloadDocument(d);
      }
    } catch (e) {
      console.error('DOWNLOAD-ALL - Error:', e);
      setSnackbarMessage('Failed to download all files. Some files may have downloaded.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Open dialog to view all files in a category
  const handleViewCategory = (catDocs, catName) => {
    setCategoryViewerDocs(catDocs || []);
    setCategoryViewerTitle(catName || 'Documents');
    setCategoryViewerOpen(true);
  };

  // Per-certificate replace (multiple-instance types). Picks one or more files and
  // uploads them as the NEXT VERSION of the certificate identified by (type, title),
  // archiving the previous version; other certificates are untouched.
  const subReplaceInputRef = React.useRef(null);
  const [subReplaceTarget, setSubReplaceTarget] = useState(null);

  const handleReplaceSub = (backendDocType, title) => {
    setSubReplaceTarget({ backendDocType, title });
    if (subReplaceInputRef.current) {
      subReplaceInputRef.current.value = '';
      subReplaceInputRef.current.click();
    }
  };

  const onSubReplaceFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    const target = subReplaceTarget;
    if (!files.length || !target) return;
    try {
      setUploading(true);
      dispatch(showLoader('Replacing certificate...'));
      setSnackbarMessage(`Replacing "${target.title}"...`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);

      const docs = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'USER_DOCUMENT');
        formData.append('documentType', target.backendDocType);
        formData.append('employeeUid', employee.uid);
        formData.append('fileName', file.name);
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
        });
        const filePath = res?.data?.data?.filePath || res?.data?.filePath;
        if (!filePath) throw new Error('Failed to resolve uploaded file path');
        // Multi-instance certificates are identified by their name; use it as the file name too
        // (suffix when replacing with more than one file).
        const certTitle = String(target.title || '').trim();
        const fname = certTitle
          ? (files.length > 1 ? `${certTitle} (${docs.length + 1})` : certTitle)
          : file.name;
        docs.push({ filePath, fileName: fname, contentType: undefined, fileSize: file.size });
      }

      await bulkSaveEmployeeDocuments({
        uid: employee.uid,
        backendDocType: target.backendDocType,
        docs,
        replaceExisting: false,
        title: target.title,
        isReplace: true,
      });

      setSnackbarMessage('Certificate replaced (new version).');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setSubReplaceTarget(null);
      await refreshDocuments({ force: true });
    } catch (err) {
      console.error('Certificate replace failed', err);
      setSnackbarMessage(err?.response?.data?.message || 'Failed to replace certificate. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      dispatch(dismissLoader());
    }
  };

  const toggleCategoryExpand = (cat) => {
    setExpandedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Trigger category-level Replace (Replace All files in category)
  const handleReplaceCategoryClick = (categoryLabel) => {
    setReplaceDialogCategory(categoryLabel);
    setReplaceDialogFiles([]);
    setReplaceDialogOpen(true);
  };

  // Save multiple files' metadata; backend honors replaceCategory by inactivating existing and setting new as active
  const saveMultipleMetadata = async ({ backendDocType, uploads, expiryDate, description, replaceCategory }) => {
    let first = true;
    const persisted = [];
    for (const u of uploads) {
      const metadataForm = new FormData();
      metadataForm.append('file', u.file);
      metadataForm.append('documentType', backendDocType);
      metadataForm.append('fileId', u.fileId);
      metadataForm.append('fileName', u.fileName);
      metadataForm.append('fileSize', String(u.fileSize));
      if (expiryDate) metadataForm.append('expiryDate', expiryDate);
      if (description) metadataForm.append('description', description);
      if (replaceCategory && first) metadataForm.append('replaceCategory', 'true');

      // Debug: verify metadata save payload key values
      console.debug('[EMPLOYEE_DETAIL] Saving metadata', {
        backendDocType,
        fileId: u.fileId,
        fileName: u.fileName,
        fileSize: u.fileSize,
        replaceCategory: !!(replaceCategory && first),
        expiryDate: !!expiryDate,
        hasFilePath: !!u.filePath,
      });

      const resp = await axios.post(`${API_BASE_V1}/users/${employee.uid}/documents`, metadataForm, {
        headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
      });
      const data = resp?.data?.data || resp?.data || {};
      // Capture final persisted filePath/id as created by backend
      persisted.push({
        id: data.id,
        filePath: data.filePath || u.filePath,
        documentType: data.documentType || backendDocType,
        fileName: data.fileName || u.fileName,
        contentType: data.contentType,
        fileSize: data.fileSize
      });
      first = false;
    }
    return persisted;
  };

  // Add files inside Replace All dialog
  const onReplaceDialogAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setReplaceDialogFiles(prev => [...prev, ...files]);
  };

  const removeReplaceDialogFile = (idx) => {
    setReplaceDialogFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Helper: bulk save employee documents (single group)
  // replaceExisting=true will deactivate existing group(s) in that category
  const bulkSaveEmployeeDocuments = async ({ uid, backendDocType, docs, sharedMetadata, replaceExisting = true, title, isReplace }) => {
    const clientBatchId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `b_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const url = `${API_BASE_V1}/employee-documents/${uid}/bulk`;
    const payload = { documentType: backendDocType, replaceExisting: !!replaceExisting, sharedMetadata, documents: docs };
    // Multiple-instance certificates: title = identity; isReplace picks add vs new-version.
    if (title) payload.title = title;
    if (isReplace) payload.isReplace = true;
    console.log('[EMPLOYEE_DETAIL][BulkSave] start', { clientBatchId, uid, backendDocType, count: docs?.length, replaceExisting, url });
    try {
      const resp = await axios.post(url, payload, { headers: addAuthHeader({ 'Content-Type': 'application/json' }) });
      const data = resp?.data?.data || resp?.data || {};
      console.log('[EMPLOYEE_DETAIL][BulkSave] success', { clientBatchId, response: data });
      return data;
    } catch (e) {
      console.error('[EMPLOYEE_DETAIL][BulkSave] error', e);
      throw e;
    }
  };

  // Map extraction -> sharedMetadata for bulk save common fields — delegated to shared util
  const buildSharedMetadataFromExtraction = buildSharedMetadataFromExtractionShared;

  // Confirm Replace All: upload, bulk save (single group + deactivate old), extraction, employee update, refresh
  const confirmReplaceAll = async () => {
    if (isReplacing) return; // guard
    if (!replaceDialogOpen || !replaceDialogCategory || !employee?.uid || replaceDialogFiles.length === 0) {
      setSnackbarMessage('Please add file(s) to replace.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    const frontendKey = toFrontendDocKey(replaceDialogCategory);
    const backendDocType = normalizeBackendDocType(frontendKey);
    console.debug('[EMPLOYEE_DETAIL] Replace-All normalized documentType', { frontendKey, backendDocType, fileCount: replaceDialogFiles.length });
    try {
      setIsReplacing(true);
      setSnackbarMessage(`Replacing ${frontendKey} with ${replaceDialogFiles.length} file(s)...`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);

      const uploads = [];
      for (const file of replaceDialogFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'USER_DOCUMENT');
        formData.append('documentType', backendDocType);
        formData.append('employeeUid', employee.uid);
        formData.append('fileName', file.name);
        // Debug: verify upload fields for replace-all
        console.debug('[EMPLOYEE_DETAIL] Uploading (replace-all)', {
          employeeUid: employee.uid,
          backendDocType,
          fileName: file.name,
          fileSize: file.size,
        });
        const res = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
        });
        const filePath = res?.data?.data?.filePath || res?.data?.filePath;
        const fileId = filePath || res?.data?.fileId;
        if (!fileId) throw new Error('Failed to resolve uploaded file identifier');
        uploads.push({ fileId, filePath: filePath || null, fileName: file.name, fileSize: file.size, file });
      }
      // Build docs for bulk save
      const docsForBulk = uploads.map(u => ({
        filePath: u.filePath,
        fileName: u.fileName,
        contentType: undefined,
        fileSize: u.fileSize,
      }));
      // Extract BEFORE bulk save using the last uploaded file path, so sharedMetadata can be sent in bulk payload
      const processingType = toProcessingDocumentType(frontendKey);
      let sharedMetadata;
      if (isExtractionSupported(processingType)) {
        try {
          const idx = uploads.length - 1;
          const u = uploads[idx];
          const data = await extractDocumentDetails({
            uid: employee.uid,
            processingType,
            filePath: u.filePath,
            documentIndex: idx,
            fileForFallback: u.file
          });
          sharedMetadata = buildSharedMetadataFromExtraction(backendDocType, data);
          console.log('[EMPLOYEE_DETAIL][ReplaceAll] extracted sharedMetadata for bulk', sharedMetadata);
        } catch (exErr) {
          console.error('Extraction failed prior to bulk save', { processingType }, exErr);
          setSnackbarMessage('Document extraction failed. Please try again.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }
      }

      // Build pending bulk save params (deferred until after confirmation dialog, if diffs found)
      const bulkParams = { uid: employee.uid, backendDocType, docs: docsForBulk, sharedMetadata };

      // If passport, compute diffs and prompt user before saving/updating profile
      if (processingType === 'PASSPORT_COPY' && sharedMetadata) {
        const upd = {};
        const diffs = [];
        const currentPassportNumber = employee.passportNumber || displayPassportNumber || null;
        if (sharedMetadata.documentNumber && sharedMetadata.documentNumber !== currentPassportNumber) {
          upd.passportNumber = sharedMetadata.documentNumber;
          diffs.push({ key: 'passportNumber', field: 'Passport Number', current: currentPassportNumber || '—', next: sharedMetadata.documentNumber });
        }
        const exExpiry = sharedMetadata.expiryDate;
        const currentPassportExpiry = employee.passportExpiry || displayPassportExpiry || null;
        if (exExpiry && exExpiry !== currentPassportExpiry) {
          upd.passportExpiry = exExpiry;
          diffs.push({ key: 'passportExpiry', field: 'Passport Expiry', current: currentPassportExpiry ? formatDate(currentPassportExpiry) : '—', next: formatDate(exExpiry) });
        }
        if (diffs.length > 0) {
          pendingBulkSaveRef.current = bulkParams;
          setPassportExtractedNew({ raw: sharedMetadata, diffs });
          setPassportUpdatePayload(upd);
          setPassportUpdatePromptOpen(true);
          // Do not close dialog yet; wait for user confirmation
          setSnackbarMessage('New passport info detected. Please confirm update.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return;
        }
      }

      // If OHC, compute diffs and prompt user before saving/updating profile
      if (processingType === 'OHC' && sharedMetadata) {
        console.log('[EMPLOYEE_DETAIL][OHC][ReplaceAll] Extracted metadata:', sharedMetadata);
        console.log('[EMPLOYEE_DETAIL][OHC][ReplaceAll] Current employee OHC data:', { ohcIssueDate: employee.ohcIssueDate, ohcExpiryDate: employee.ohcExpiryDate });
        const upd = {};
        const diffs = [];
        if (sharedMetadata.issueDate && sharedMetadata.issueDate !== employee.ohcIssueDate) {
          upd.ohcIssueDate = sharedMetadata.issueDate;
          diffs.push({ key: 'ohcIssueDate', field: 'OHC Issue Date', current: employee.ohcIssueDate ? formatDate(employee.ohcIssueDate) : '—', next: formatDate(sharedMetadata.issueDate) });
        }
        if (sharedMetadata.expiryDate && sharedMetadata.expiryDate !== employee.ohcExpiryDate) {
          upd.ohcExpiryDate = sharedMetadata.expiryDate;
          diffs.push({ key: 'ohcExpiryDate', field: 'OHC Expiry Date', current: employee.ohcExpiryDate ? formatDate(employee.ohcExpiryDate) : '—', next: formatDate(sharedMetadata.expiryDate) });
        }
        console.log('[EMPLOYEE_DETAIL][OHC][ReplaceAll] Computed diffs:', diffs);
        if (diffs.length > 0) {
          pendingBulkSaveRef.current = bulkParams;
          setOhcExtractedNew({ raw: sharedMetadata, diffs });
          setOhcUpdatePayload(upd);
          setOhcUpdatePromptOpen(true);
          setSnackbarMessage('New OHC info detected. Please confirm update.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return;
        }
      }

      // If Driving License, compute diffs and prompt user before saving/updating profile
      if (processingType === 'DRIVING_LICENSE' && sharedMetadata) {
        console.log('[EMPLOYEE_DETAIL][DL][ReplaceAll] Extracted metadata:', sharedMetadata);
        console.log('[EMPLOYEE_DETAIL][DL][ReplaceAll] Current employee DL data:', {
          drivingLicenseIssueDate: employee.drivingLicenseIssueDate,
          drivingLicenseExpiryDate: employee.drivingLicenseExpiryDate,
          drivingLicensePlaceOfIssue: employee.drivingLicensePlaceOfIssue
        });
        const upd = {};
        const diffs = [];
        if (sharedMetadata.issueDate && sharedMetadata.issueDate !== employee.drivingLicenseIssueDate) {
          upd.drivingLicenseIssueDate = sharedMetadata.issueDate;
          diffs.push({ key: 'drivingLicenseIssueDate', field: 'Driving License Issue Date', current: employee.drivingLicenseIssueDate ? formatDate(employee.drivingLicenseIssueDate) : '—', next: formatDate(sharedMetadata.issueDate) });
        }
        if (sharedMetadata.expiryDate && sharedMetadata.expiryDate !== employee.drivingLicenseExpiryDate) {
          upd.drivingLicenseExpiryDate = sharedMetadata.expiryDate;
          diffs.push({ key: 'drivingLicenseExpiryDate', field: 'Driving License Expiry Date', current: employee.drivingLicenseExpiryDate ? formatDate(employee.drivingLicenseExpiryDate) : '—', next: formatDate(sharedMetadata.expiryDate) });
        }
        if (sharedMetadata.placeOfIssue && sharedMetadata.placeOfIssue !== employee.drivingLicensePlaceOfIssue) {
          upd.drivingLicensePlaceOfIssue = sharedMetadata.placeOfIssue;
          diffs.push({ key: 'drivingLicensePlaceOfIssue', field: 'Place of Issue', current: employee.drivingLicensePlaceOfIssue || '—', next: sharedMetadata.placeOfIssue });
        }
        console.log('[EMPLOYEE_DETAIL][DL][ReplaceAll] Computed diffs:', diffs);
        if (diffs.length > 0) {
          pendingBulkSaveRef.current = bulkParams;
          setDrivingLicenseExtractedNew({ raw: sharedMetadata, diffs });
          setDrivingLicenseUpdatePayload(upd);
          setDrivingLicenseUpdatePromptOpen(true);
          setSnackbarMessage('New Driving License info detected. Please confirm update.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return;
        }
      }

      // No diffs found (or non-extractable type) — perform bulk save immediately
      const bulkResult = await bulkSaveEmployeeDocuments(bulkParams);
      const persistedDocs = Array.isArray(bulkResult?.documents) ? bulkResult.documents : (Array.isArray(bulkResult) ? bulkResult : []);

      // Refresh view:
      // - force grouped reload for PASSPORT (needs common metadata)
      // - force grouped reload for EMIRATES_ID, VISA_PAGE, and OHC to reflect extracted metadata
      // - otherwise, update locally
      if (processingType === 'PASSPORT_COPY') {
        await refreshDocuments({ force: true });
      } else if (processingType === 'EMIRATES_ID' || processingType === 'VISA_PAGE' || processingType === 'OHC' || processingType === 'DRIVING_LICENSE') {
        await refreshDocuments({ force: true });
      } else {
        setNewStorageDocuments((prev) => {
          const backendTypeKey = normalizeBackendDocType(processingType);
          const filtered = Array.isArray(prev)
            ? prev.filter((d) => normalizeBackendDocType(d.documentType || d.type || d.category) !== backendTypeKey)
            : [];
          const added = (persistedDocs || []).map((pd, i) => ({
            id: pd.id,
            uid: employee.uid,
            documentType: processingType,
            fileName: uploads[i]?.fileName || pd.fileName || 'document',
            filePath: pd.filePath || uploads[i]?.filePath || null,
            contentType: pd.contentType,
            size: pd.fileSize || uploads[i]?.fileSize,
            expiryDate: undefined,
            createdAt: pd.createdAt,
            updatedAt: pd.updatedAt,
            active: true,
            isCurrent: true,
            isPrimary: undefined,
            status: 'Valid',
            documentNumber: undefined,
            metadata: undefined,
            _raw: { ...pd },
          }));
          return [...added, ...filtered];
        });
      }

      setSnackbarMessage('Category replaced successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setReplaceDialogOpen(false);
      setReplaceDialogFiles([]);
      setReplaceDialogCategory(null);
    } catch (err) {
      console.error('Replace Category error:', err);
      setSnackbarMessage('Failed to replace category. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsReplacing(false);
    }
  };

  const detailRoutes = isStakeholder ? ROUTES.CUSTOMER.STAKEHOLDERS : ROUTES.CUSTOMER.EMPLOYEES;

  // Handle navigation back to list
  const handleBackToList = () => {
    navigate(detailRoutes.LIST);
  };

  // Handle edit
  const handleEditEmployee = () => {
    navigate(detailRoutes.EDIT(id));
  };

  // Handle manage documents
  const handleManageDocuments = () => {
    navigate(isStakeholder ? detailRoutes.DOCUMENT_PROFILE(employee?.uid || id) : ROUTES.CUSTOMER.EMPLOYEES.DOCUMENTS(id));
  };

  // Handle file selection for upload (supports multi-select and incremental add)
  const handleFileChange = (e) => {
    const filesList = e.target.files ? Array.from(e.target.files) : [];
    if (filesList.length > 0) {
      const existing = Array.isArray(uploadData.files) ? uploadData.files : [];
      const merged = [...existing, ...filesList];
      setUploadData({
        ...uploadData,
        file: uploadData.file || filesList[0], // keep existing first if present
        files: merged,
      });

      // Clear file error if it exists
      if (uploadErrors.file) {
        setUploadErrors({
          ...uploadErrors,
          file: null
        });
      }
    }
    // allow selecting the same file again by resetting input value
    if (e && e.target) {
      e.target.value = '';
    }
  };

  // Handle upload form field changes
  const handleUploadChange = (field) => (e) => {
    const value = e.target.value;
    setUploadData({
      ...uploadData,
      [field]: value
    });
    
    // Clear error for this field when changed
    if (uploadErrors[field]) {
      setUploadErrors({
        ...uploadErrors,
        [field]: null
      });
    }
  };

  // Open upload dialog
  const handleOpenUploadDialog = () => {
    setUploadData({
      file: null,
      files: [],
      category: '', // Require explicit selection (Option A)
      description: ''
    });
    setUploadErrors({});
    setOpenUploadDialog(true);
  };

  // Close upload dialog
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  // Validate upload form
  const validateUpload = () => {
    const errors = {};
    const hasFiles = (uploadData.files && uploadData.files.length > 0) || !!uploadData.file;
    if (!hasFiles) {
      errors.file = 'Please select at least one file to upload';
    }
    if (!uploadData.category) {
      errors.category = 'Document category is required';
    }
    // Multiple-instance certificates (educational/experience) need a name so each
    // sub-file is identifiable in the list.
    if (MULTI_INSTANCE_DOC_TYPES.includes(uploadData.category) && !String(uploadData.documentName || '').trim()) {
      errors.documentName = 'Document name is required';
    }
    return errors;
  };

  // Perform upload(s) for selected file(s) in the dialog
  const handleUpload = async () => {
    const errs = validateUpload();
    setUploadErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Preflight: verify employee/uid existence (be tolerant: allow if either users/{uid} OR employees/{id} exists)
    try {
      if (!employee?.uid) {
        setSnackbarMessage('Missing employee UID. Please refresh and try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      let ok = false;
      try {
        await axios.get(API_ENDPOINTS.USERS.GET(employee.uid), { headers: addAuthHeader() });
        ok = true;
      } catch (e1) {
        // If users/{uid} 404s (e.g., non-login employee), try employees/{id}
        try {
          if (id) {
            await axios.get(API_ENDPOINTS.EMPLOYEES.DETAIL(id), { headers: addAuthHeader() });
            ok = true;
          }
        } catch (e2) {
          console.warn('[EMPLOYEE_DETAIL] employees/{id} check also failed', e2);
        }
        if (!ok) {
          throw e1; // surface original error
        }
      }
    } catch (err) {
      console.error('[EMPLOYEE_DETAIL] Preflight existence check failed', err);
      const msg = err?.response?.status === 404
        ? `Record not found for UID ${employee.uid}. Please verify the employee or update the UID.`
        : (err?.response?.data?.message || err?.message || 'Failed to verify employee.');
      setSnackbarMessage(msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setUploading(true);
      const files = (uploadData.files && uploadData.files.length > 0)
        ? uploadData.files
        : (uploadData.file ? [uploadData.file] : []);

      const frontendKey = uploadData.category;
      const backendDocType = normalizeBackendDocType(frontendKey);
      const processingType = toProcessingDocumentType(frontendKey);

      const uploads = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'USER_DOCUMENT');
        formData.append('documentType', backendDocType);
        formData.append('employeeUid', employee.uid);
        formData.append('fileName', file.name);
        const res = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
        });
        const filePath = res?.data?.data?.filePath || res?.data?.filePath;
        const fileId = filePath || res?.data?.fileId;
        if (!fileId) throw new Error('Failed to resolve uploaded file identifier');
        uploads.push({ fileId, filePath: filePath || null, fileName: file.name, fileSize: file.size, file });
      }

      // Try extraction on the last uploaded file to prepare shared metadata when applicable
      let sharedMetadata;
      if (isExtractionSupported(processingType) && uploads.length > 0) {
        try {
          const u = uploads[uploads.length - 1];
          const data = await extractDocumentDetails({
            uid: employee.uid,
            processingType,
            filePath: u.filePath,
            documentIndex: uploads.length - 1,
            fileForFallback: u.file
          });
          sharedMetadata = buildSharedMetadataFromExtraction(backendDocType, data?.data || data || {});
        } catch (e) {
          console.warn('[EMPLOYEE_DETAIL] Extraction failed, continuing without shared metadata', e);
        }
      }

      // Build docs for bulk save (do not replace existing for new uploads).
      const isMultiCert = MULTI_INSTANCE_DOC_TYPES.includes(backendDocType);
      const certName = String(uploadData.documentName || '').trim();
      // For multiple-instance certificates, also use the entered name as the file name so the saved
      // file is identified by the certificate name (suffix when a version holds more than one file).
      const docsForBulk = uploads.map((u, idx) => ({
        filePath: u.filePath,
        fileName: (isMultiCert && certName)
          ? (uploads.length > 1 ? `${certName} (${idx + 1})` : certName)
          : u.fileName,
        contentType: undefined,
        fileSize: u.fileSize,
      }));

      // For multiple-instance certificates the name is the certificate identity
      // (title); this is a NEW certificate, so isReplace=false (backend rejects a
      // duplicate name).
      await bulkSaveEmployeeDocuments({
        uid: employee.uid,
        backendDocType,
        docs: docsForBulk,
        sharedMetadata,
        replaceExisting: false,
        title: isMultiCert ? certName : undefined,
        isReplace: false
      });

      // If OHC, compute diffs and prompt user before updating profile using extracted data
      if (processingType === 'OHC' && sharedMetadata) {
        console.log('[EMPLOYEE_DETAIL][OHC][Upload] Extracted metadata:', sharedMetadata);
        console.log('[EMPLOYEE_DETAIL][OHC][Upload] Current employee OHC data:', { ohcIssueDate: employee.ohcIssueDate, ohcExpiryDate: employee.ohcExpiryDate });
        const upd = {};
        const diffs = [];
        if (sharedMetadata.issueDate && sharedMetadata.issueDate !== employee.ohcIssueDate) {
          upd.ohcIssueDate = sharedMetadata.issueDate;
          diffs.push({ key: 'ohcIssueDate', field: 'OHC Issue Date', current: employee.ohcIssueDate ? formatDate(employee.ohcIssueDate) : '—', next: formatDate(sharedMetadata.issueDate) });
        }
        if (sharedMetadata.expiryDate && sharedMetadata.expiryDate !== employee.ohcExpiryDate) {
          upd.ohcExpiryDate = sharedMetadata.expiryDate;
          diffs.push({ key: 'ohcExpiryDate', field: 'OHC Expiry Date', current: employee.ohcExpiryDate ? formatDate(employee.ohcExpiryDate) : '—', next: formatDate(sharedMetadata.expiryDate) });
        }
        console.log('[EMPLOYEE_DETAIL][OHC][Upload] Computed diffs:', diffs);
        if (diffs.length > 0) {
          setOhcExtractedNew({ raw: sharedMetadata, diffs });
          setOhcUpdatePayload(upd);
          setOhcUpdatePromptOpen(true);
          setSnackbarMessage('New OHC info detected. Please confirm update.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          setOpenUploadDialog(false);
          return;
        }
      }

      // If Driving License, compute diffs and prompt user before updating profile using extracted data
      if (processingType === 'DRIVING_LICENSE' && sharedMetadata) {
        console.log('[EMPLOYEE_DETAIL][DL][Upload] Extracted metadata:', sharedMetadata);
        console.log('[EMPLOYEE_DETAIL][DL][Upload] Current employee DL data:', { 
          drivingLicenseIssueDate: employee.drivingLicenseIssueDate, 
          drivingLicenseExpiryDate: employee.drivingLicenseExpiryDate,
          drivingLicensePlaceOfIssue: employee.drivingLicensePlaceOfIssue
        });
        const upd = {};
        const diffs = [];
        if (sharedMetadata.issueDate && sharedMetadata.issueDate !== employee.drivingLicenseIssueDate) {
          upd.drivingLicenseIssueDate = sharedMetadata.issueDate;
          diffs.push({ key: 'drivingLicenseIssueDate', field: 'Driving License Issue Date', current: employee.drivingLicenseIssueDate ? formatDate(employee.drivingLicenseIssueDate) : '—', next: formatDate(sharedMetadata.issueDate) });
        }
        if (sharedMetadata.expiryDate && sharedMetadata.expiryDate !== employee.drivingLicenseExpiryDate) {
          upd.drivingLicenseExpiryDate = sharedMetadata.expiryDate;
          diffs.push({ key: 'drivingLicenseExpiryDate', field: 'Driving License Expiry Date', current: employee.drivingLicenseExpiryDate ? formatDate(employee.drivingLicenseExpiryDate) : '—', next: formatDate(sharedMetadata.expiryDate) });
        }
        if (sharedMetadata.placeOfIssue && sharedMetadata.placeOfIssue !== employee.drivingLicensePlaceOfIssue) {
          upd.drivingLicensePlaceOfIssue = sharedMetadata.placeOfIssue;
          diffs.push({ key: 'drivingLicensePlaceOfIssue', field: 'Place of Issue', current: employee.drivingLicensePlaceOfIssue || '—', next: sharedMetadata.placeOfIssue });
        }
        console.log('[EMPLOYEE_DETAIL][DL][Upload] Computed diffs:', diffs);
        if (diffs.length > 0) {
          setDrivingLicenseExtractedNew({ raw: sharedMetadata, diffs });
          setDrivingLicenseUpdatePayload(upd);
          setDrivingLicenseUpdatePromptOpen(true);
          setSnackbarMessage('New Driving License info detected. Please confirm update.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          setOpenUploadDialog(false);
          return;
        }
      }

      // Force refresh to display new docs and reflect any extraction-based updates
      await refreshDocuments({ force: true });

      setSnackbarMessage(`Uploaded ${uploads.length} file(s) for ${frontendKey}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenUploadDialog(false);
    } catch (e) {
      console.error('[EMPLOYEE_DETAIL] Upload error', e);
      setSnackbarMessage(e?.response?.data?.message || e?.message || 'Upload failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  // Handle open UID update dialog
  const handleOpenUidDialog = () => {
    if (employee && employee.uid) {
      setNewUid(employee.uid);
    } else {
      setNewUid('');
    }
    setUidError('');
    setUidDialogOpen(true);
  };

  // Handle UID dialog close
  const handleUidDialogClose = () => {
    setUidDialogOpen(false);
    setUidError('');
  };

  // Validate UID format
  const validateUid = (uid) => {
    if (!uid || uid.trim() === '') {
      setUidError('UID cannot be empty');
      return false;
    }
    
    const trimmedUid = uid.trim();
    
    // Check format - allow alphanumeric characters, hyphens, and underscores
    if (!trimmedUid.match(/^[a-zA-Z0-9_-]+$/)) {
      setUidError('UID must contain only alphanumeric characters, hyphens, and underscores');
      return false;
    }
    
    // Check length
    if (trimmedUid.length > 50) {
      setUidError('UID must be 50 characters or less');
      return false;
    }
    
    setUidError('');
    return true;
  };

  // Handle UID update
  const handleUidUpdate = async () => {
    if (!validateUid(newUid)) {
      return;
    }
    
    try {
      setUidUpdateLoading(true);
      
      // Get auth token from local storage (try multiple possible keys)
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('jwt_token') || 
                    localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await axios.put(
        API_ENDPOINTS.EMPLOYEES.UPDATE_UID(id),
        { uid: newUid.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.success) {
        // Update employee state with new data
        setEmployee(response.data.data);
        setSnackbarMessage('Employee UID updated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleUidDialogClose();
      } else {
        throw new Error(response.data?.message || 'Failed to update UID');
      }
    } catch (err) {
      console.error('Error updating employee UID:', err);
      setUidError(err.response?.data?.message || err.message || 'Failed to update UID');
      setSnackbarMessage(err.response?.data?.message || err.message || 'Failed to update UID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUidUpdateLoading(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Show error state
  if (error || !employee) {
    // Handle closing error alert
    const handleCloseError = () => {
      setError(null);
    };

    // Handle closing success alert
    const handleCloseSuccess = () => {
      setSuccess(null);
    };
    
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Error Notification */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        
        {/* Success Notification */}
        <Snackbar 
          open={!!success} 
          autoHideDuration={6000} 
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
        <Alert severity="error">
          {error || 'Failed to load employee details'}
        </Alert>
        <Box mt={2}>
          <Button variant="contained" onClick={handleBackToList}>
            Back to Employees
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      {/* UID Update Dialog */}
      <Dialog open={uidDialogOpen} onClose={handleUidDialogClose}>
        <DialogTitle>Update Employee UID</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a new UID for this employee. The UID must contain only alphanumeric characters, hyphens, and underscores, and be no longer than 50 characters.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Employee UID"
            type="text"
            fullWidth
            variant="outlined"
            value={newUid}
            onChange={(e) => setNewUid(e.target.value)}
            error={!!uidError}
            helperText={uidError}
            disabled={uidUpdateLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUidDialogClose} disabled={uidUpdateLoading}>Cancel</Button>
          <Button 
            onClick={handleUidUpdate} 
            variant="contained" 
            disabled={uidUpdateLoading}
            startIcon={uidUpdateLoading ? <CircularProgress size={20} /> : null}
          >
            {uidUpdateLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Passport Update Confirmation Dialog */}
      <Dialog
        open={passportUpdatePromptOpen}
        onClose={cancelPassportUpdates}
        fullWidth
        maxWidth="sm"
        aria-labelledby="passport-update-dialog-title"
      >
        <DialogTitle id="passport-update-dialog-title" sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Update Passport Info</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
            We detected new passport information from the uploaded file. Review the changes below and confirm if you want to update the employee profile.
          </DialogContentText>
          {passportExtractedNew?.diffs && passportExtractedNew.diffs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Field</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Current</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {passportExtractedNew.diffs.map((d, i) => (
                  <TableRow key={`pdiff-${i}`}>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.field}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.current}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {passportEditMode?.[d.key] ? (
                          <>
                            <TextField
                              size="small"
                              type={d.key === 'passportExpiry' ? 'date' : 'text'}
                              value={String((passportUpdatePayload?.[d.key] ?? '').toString ? passportUpdatePayload?.[d.key] : (passportUpdatePayload?.[d.key] ?? '')) || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPassportUpdatePayload(prev => ({ ...(prev || {}), [d.key]: val }));
                              }}
                              sx={{ minWidth: d.key === 'passportExpiry' ? 170 : 220 }}
                              inputProps={{ style: { fontSize: '0.9rem' } }}
                            />
                            <Button
                              onClick={() => setPassportEditMode(prev => ({ ...prev, [d.key]: false }))}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                              Done
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {(() => {
                                const val = passportUpdatePayload?.[d.key];
                                if (d.key === 'passportExpiry') {
                                  // Show formatted for readability when not editing
                                  return formatDate(val || d.next);
                                }
                                return val ?? d.next;
                              })()}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setPassportEditMode(prev => ({ ...prev, [d.key]: true }))}
                                sx={{ ml: 0.5 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No changes detected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPassportUpdates} sx={{ fontSize: '0.85rem' }}>Keep Existing</Button>
          <Button variant="contained" color="primary" onClick={applyPassportUpdates} sx={{ fontSize: '0.85rem' }}>Update Profile</Button>
        </DialogActions>
      </Dialog>

      {/* OHC Update Confirmation Dialog */}
      <Dialog
        open={ohcUpdatePromptOpen}
        onClose={cancelOhcUpdates}
        fullWidth
        maxWidth="sm"
        aria-labelledby="ohc-update-dialog-title"
      >
        <DialogTitle id="ohc-update-dialog-title" sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Update OHC Info</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
            We detected new OHC information from the uploaded file. Review the changes below and confirm if you want to update the employee profile.
          </DialogContentText>
          {ohcExtractedNew?.diffs && ohcExtractedNew.diffs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Field</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Current</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ohcExtractedNew.diffs.map((d, i) => (
                  <TableRow key={`ohcdiff-${i}`}>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.field}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.current}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ohcEditMode?.[d.key] ? (
                          <>
                            <TextField
                              size="small"
                              type="date"
                              value={String((ohcUpdatePayload?.[d.key] ?? '').toString ? ohcUpdatePayload?.[d.key] : (ohcUpdatePayload?.[d.key] ?? '')) || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setOhcUpdatePayload(prev => ({ ...(prev || {}), [d.key]: val }));
                              }}
                              sx={{ minWidth: 170 }}
                              inputProps={{ style: { fontSize: '0.9rem' } }}
                            />
                            <Button
                              onClick={() => setOhcEditMode(prev => ({ ...prev, [d.key]: false }))}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                              Done
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {formatDate(ohcUpdatePayload?.[d.key] || d.next)}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setOhcEditMode(prev => ({ ...prev, [d.key]: true }))}
                                sx={{ ml: 0.5 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No changes detected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelOhcUpdates} sx={{ fontSize: '0.85rem' }}>Keep Existing</Button>
          <Button variant="contained" color="primary" onClick={applyOhcUpdates} sx={{ fontSize: '0.85rem' }}>Update Profile</Button>
        </DialogActions>
      </Dialog>

      {/* Driving License Update Confirmation Dialog */}
      <Dialog
        open={drivingLicenseUpdatePromptOpen}
        onClose={cancelDrivingLicenseUpdates}
        fullWidth
        maxWidth="sm"
        aria-labelledby="dl-update-dialog-title"
      >
        <DialogTitle id="dl-update-dialog-title" sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Update Driving License Info</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
            We detected new Driving License information from the uploaded file. Review the changes below and confirm if you want to update the employee profile.
          </DialogContentText>
          {drivingLicenseExtractedNew?.diffs && drivingLicenseExtractedNew.diffs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Field</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>Current</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drivingLicenseExtractedNew.diffs.map((d, i) => (
                  <TableRow key={`dldiff-${i}`}>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.field}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>{d.current}</TableCell>
                    <TableCell sx={{ fontSize: '0.9rem', py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {drivingLicenseEditMode?.[d.key] ? (
                          <>
                            <TextField
                              size="small"
                              type={d.key === 'drivingLicensePlaceOfIssue' ? 'text' : 'date'}
                              value={String((drivingLicenseUpdatePayload?.[d.key] ?? '').toString ? drivingLicenseUpdatePayload?.[d.key] : (drivingLicenseUpdatePayload?.[d.key] ?? '')) || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDrivingLicenseUpdatePayload(prev => ({ ...(prev || {}), [d.key]: val }));
                              }}
                              sx={{ minWidth: d.key === 'drivingLicensePlaceOfIssue' ? 220 : 170 }}
                              inputProps={{ style: { fontSize: '0.9rem' } }}
                            />
                            <Button
                              onClick={() => setDrivingLicenseEditMode(prev => ({ ...prev, [d.key]: false }))}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                            >
                              Done
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {d.key === 'drivingLicensePlaceOfIssue' 
                                ? (drivingLicenseUpdatePayload?.[d.key] || d.next)
                                : formatDate(drivingLicenseUpdatePayload?.[d.key] || d.next)}
                            </Typography>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => setDrivingLicenseEditMode(prev => ({ ...prev, [d.key]: true }))}
                                sx={{ ml: 0.5 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No changes detected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDrivingLicenseUpdates} sx={{ fontSize: '0.85rem' }}>Keep Existing</Button>
          <Button variant="contained" color="primary" onClick={applyDrivingLicenseUpdates} sx={{ fontSize: '0.85rem' }}>Update Profile</Button>
        </DialogActions>
      </Dialog>
      
      {/* Replace All Dialog */}
      <Dialog
        open={replaceDialogOpen}
        onClose={() => setReplaceDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="replace-all-dialog-title"
      >
        <DialogTitle id="replace-all-dialog-title">
          Replace All: {String(replaceDialogCategory || '').toString()}
          <IconButton
            aria-label="close"
            onClick={() => setReplaceDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box>
            <input
              type="file"
              multiple
              ref={replaceDialogInputRef}
              onChange={onReplaceDialogAddFiles}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              onClick={() => {
                if (replaceDialogInputRef.current) {
                  replaceDialogInputRef.current.value = '';
                  replaceDialogInputRef.current.click();
                }
              }}
              sx={{ mb: 2 }}
            >
              Add Files
            </Button>
            {replaceDialogFiles.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No files added yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {replaceDialogFiles.map((f, idx) => (
                    <TableRow key={`rf-${idx}`}>
                      <TableCell>{f.name}</TableCell>
                      <TableCell>{formatFileSize(f.size)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" color="error" onClick={() => removeReplaceDialogFile(idx)}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmReplaceAll} disabled={replaceDialogFiles.length === 0}>
            Confirm Replace
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Viewer Modal */}
      <Dialog
        open={categoryViewerOpen}
        onClose={() => setCategoryViewerOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="category-viewer-dialog-title"
      >
        <DialogTitle id="category-viewer-dialog-title">
          {categoryViewerTitle || 'Documents'}
          <IconButton
            aria-label="close"
            onClick={() => setCategoryViewerOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {(!categoryViewerDocs || categoryViewerDocs.length === 0) ? (
            <Typography variant="body2">No files in this category.</Typography>
          ) : (
            <>
            <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '22%' }}>File Name</TableCell>
                  <TableCell sx={{ width: '13%' }}>Uploaded</TableCell>
                  <TableCell sx={{ width: '14%' }}>Expiry</TableCell>
                  <TableCell sx={{ width: '9%' }}>Size</TableCell>
                  <TableCell sx={{ width: '10%' }}>Status</TableCell>
                  <TableCell sx={{ width: '12%' }}>Ticket</TableCell>
                  <TableCell align="right" sx={{ width: '20%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryViewerDocs.map((doc) => {
                  const daysUntilExpiry = doc.expiryDate ? calculateDaysUntilExpiry(doc.expiryDate) : null;
                  return (
                    <TableRow key={`cv-${doc.id}`} hover>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {doc.fileName || doc.name || 'Document'}
                        {doc.version ? <Chip label={`v${doc.version}`} size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} /> : null}
                      </TableCell>
                      <TableCell>{formatDate(doc.uploadDate || doc.createdAt, true)}</TableCell>
                      <TableCell>
                        {doc.expiryDate ? (
                          <>
                            {formatDate(doc.expiryDate)}
                            {daysUntilExpiry > 0 && daysUntilExpiry < 30 && (
                              <> <Warning fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} /> {daysUntilExpiry} days left</>
                            )}
                          </>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>{doc.size ? formatFileSize(doc.size) : 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={
                            doc.status === 'Valid' ? 'success' :
                            doc.status === 'Expired' ? 'error' :
                            doc.status === 'Expiring Soon' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {doc.ticket ? (
                          <Chip label={doc.ticket.orgTicketNumber || `#${doc.ticket.ticketNumber}`} size="small" color="info" variant="outlined" />
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<Visibility />} onClick={() => handleViewDocument(doc)} sx={{ mr: 1 }}>
                          Preview
                        </Button>
                        <Button size="small" startIcon={<CloudDownload />} onClick={() => handleDownloadDocument(doc)}>
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {categoryViewerDocs.some(d => d.ticket) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Ticket Information</Typography>
                {[...new Map(categoryViewerDocs.filter(d => d.ticket).map(d => [d.ticket.id || d.ticket.ticketNumber, d.ticket])).values()].map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="body2"><strong>Ticket:</strong> {t.orgTicketNumber || `#${t.ticketNumber}`}</Typography>
                    {t.serviceName && <Typography variant="body2"><strong>Service:</strong> {t.serviceName}</Typography>}
                    {t.status && <Typography variant="body2"><strong>Status:</strong> {t.status}</Typography>}
                    {t.createdBy && <Typography variant="body2"><strong>Created By:</strong> {t.createdBy}</Typography>}
                  </Box>
                ))}
              </Box>
            )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryViewerOpen(false)}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDownloadCategory(categoryViewerDocs, categoryViewerTitle)}
          >
            Download All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden multi-file input for per-certificate replace (educational / experience) */}
      <input
        ref={subReplaceInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={onSubReplaceFilesChosen}
      />
      
      <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
        <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" component="h1">
              {isStakeholder ? 'Stakeholder Profile' : 'Employee Profile'}
            </Typography>
          </Box>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<AttachFile />}
              sx={{ mr: 1 }}
              onClick={handleManageDocuments}
            >
              Manage Documents
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Edit />}
              onClick={handleEditEmployee}
            >
              {isStakeholder ? 'Edit Stakeholder' : 'Edit Employee'}
            </Button>
          </Box>
        </Box>

        <Card sx={{ mb: 4 }}>
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {profilePhotoThumb ? (
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  mr: 2,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={profilePhotoThumb}
                    alt={`${employee.firstName || ''} ${employee.lastName || ''} photo`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ) : profilePhotoUrl ? (
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  mr: 2,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}>
                  <AuthenticatedImage
                    src={profilePhotoUrl}
                    alt={`${employee.firstName || ''} ${employee.lastName || ''} photo`}
                    maxSize={100}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.light', 
                  color: 'primary.contrastText', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Person sx={{ fontSize: 36 }} />
                </Box>
              )}
              <Box>
                <Typography variant="h5">
                  {employee.firstName} {employee.lastName}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {employee.positionName} • {employee.departmentName}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={statusLabel} 
              color={
                employee.status === 'ACTIVE' ? 'success' :
                employee.status === 'DEACTIVATED' ? 'error' :
                employee.status === 'ABSCONDED' ? 'warning' : 'default'
              }
              sx={{ px: 1 }}
            />
          </Box>
          
          <Divider />
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="employee tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Personal Information" icon={<Person />} iconPosition="start" />
              <Tab label="Documents" icon={<InsertDriveFile />} iconPosition="start" />
              <Tab label={isStakeholder ? "Stakeholder Details" : "Employment Details"} icon={<BusinessCenter />} iconPosition="start" />
              <Tab label="Account Access" icon={<Security />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Personal Information Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={1}>
              {/* Contact Information - First Row */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 500,
                      color: '#1a3e72'
                    }}
                  >
                    Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <Email color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Email</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.email}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <Phone color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Phone</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.phone || employee.mobileNumber || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <LocationOn color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Address</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.address || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Personal Details - Second Row */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 500,
                      color: '#1a3e72'
                    }}
                  >
                    Personal Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <Cake color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Date of Birth</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{formatDate(employee.dateOfBirth)}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <Badge color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>National ID</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.nationalId || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <VerifiedUser color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Passport Number</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{ employee.passportNumber || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <VerifiedUser color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Passport Expiry</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{ employee.passportExpiry ? formatDate(employee.passportExpiry) : 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <Work color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Profession</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>
                            {employee.profession?.title || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <Public color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Nationality</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>
                            {employee.nationality || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <LocalHospital color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>OHC Required</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>
                            {employee.requireOhc ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center">
                        <DriveEta color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Driving License Required</Typography>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>
                            {employee.requireDrivingLicense ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: '1rem', 
                        fontWeight: 500,
                        color: '#1a3e72' // Darker blue color for better visibility
                      }}
                    >
                      {isStakeholder ? 'Stakeholder Identifier' : 'Employee Identifier'}
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Edit />} 
                      onClick={handleOpenUidDialog}
                      size="small"
                      sx={{ 
                        backgroundColor: '#1a3e72', 
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#15325d'
                        }
                      }}
                    >
                      Manage UID
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Employee ID</Typography>
                        <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.employeeId || 'Not assigned'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>UID</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>
                            {employee.userId || 'Not assigned'}
                          </Typography>
                          <Chip 
                            label="System UID" 
                            size="small" 
                            color="info" 
                            sx={{ ml: 1, height: 20, fontSize: '0.75rem' }} 
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {isStakeholder ? 'Stakeholder Documents' : 'Employee Documents'}
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AttachFile />}
                onClick={handleOpenUploadDialog}
              >
                Upload New Document
              </Button>
            </Box>
            
            {(() => {
              // Build unified documents list from new storage and legacy employee.documents
              const nsDocs = Array.isArray(newStorageDocuments) ? newStorageDocuments : [];
              const legacyDocsRaw = Array.isArray(employee?.documents) ? employee.documents : [];
              const legacyDocs = legacyDocsRaw.map((doc) => {
                const expiry = doc.expiryDate || doc.expiry || doc.expirationDate || doc.expireDate || null;
                const created = doc.createdAt || doc.createDateTime || doc.createDate || null;
                const updated = doc.updatedAt || doc.updateDateTime || doc.modifiedAt || null;
                const normalizeActive = (val) => {
                  if (typeof val === 'boolean') return val;
                  if (typeof val === 'string') return val.toLowerCase() === 'true';
                  if (typeof val === 'number') return val === 1;
                  return false;
                };
                return {
                  id: doc.id,
                  uid: doc.uid || employee?.uid,
                  documentType: doc.documentType || doc.type || doc.category,
                  fileName: doc.fileName || doc.name || 'document',
                  filePath: doc.filePath || null,
                  contentType: doc.contentType,
                  size: doc.fileSize,
                  expiryDate: expiry,
                  createdAt: created,
                  updatedAt: updated,
                  active: normalizeActive(doc.active || doc.isCurrent),
                  isPrimary: typeof doc.isPrimary === 'boolean' ? doc.isPrimary : undefined,
                  status: expiry ? (calculateDaysUntilExpiry(expiry) < 0 ? 'Expired' : (calculateDaysUntilExpiry(expiry) <= 180 ? 'Expiring Soon' : 'Valid')) : 'Valid',
                  _raw: doc,
                  _source: 'legacy'
                };
              });
              // Prefer new storage documents exclusively if available to avoid showing stale legacy groups.
              // Fall back to legacy documents (filtered to current/active) only when new storage is empty.
              let docs;
              if (nsDocs.length > 0) {
                docs = nsDocs.slice();
              } else {
                const legacyCurrent = legacyDocs.filter(d => d.active === true || d.isCurrent === true || d._raw?.isCurrent === true);
                docs = legacyCurrent;
              }
              if (!docs.length) {
                return (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="textSecondary">
                      No documents available for this employee.
                    </Typography>
                  </Paper>
                );
              }

              // Group documents by normalized backend category/type to avoid duplicates (e.g., PASSPORT_COPY vs passport_copy)
              const groups = docs.reduce((acc, d) => {
                const raw = d.documentType || d.type || d.category || 'Uncategorized';
                const key = normalizeBackendDocType(raw); // backend snake_case
                if (!acc[key]) acc[key] = [];
                acc[key].push(d);
                return acc;
              }, {});

              const categoryKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));

              // Aggregate global common fields across all items for cross-category display (e.g., passport details)
              const globalCommon = (() => {
                const merged = {};
                for (const arr of Object.values(groups)) {
                  for (const it of arr) {
                    const a = it?._raw?.common || {};
                    const b = it?._raw || {};
                    const c = it?.metadata?.common || {};
                    for (const src of [a, b, c]) {
                      if (src && typeof src === 'object') {
                        Object.keys(src).forEach((k) => {
                          if (merged[k] == null && src[k] != null && src[k] !== '') merged[k] = src[k];
                        });
                      }
                    }
                  }
                }
                return merged;
              })();
              const globalPassportNumber = globalCommon?.passportNumber
                || globalCommon?.passport_no
                || globalCommon?.passportNo
                || globalCommon?.documentNumber
                || undefined;
              const globalPassportExpiry = globalCommon?.passportExpiry
                || globalCommon?.passport_expiry
                || globalCommon?.expiryDate
                || undefined;

              // Current documents for a category (prefer isCurrent; else highest version).
              const currentItemsFor = (arr) => {
                const explicit = arr.filter(i => i.isCurrent === true || i.current === true || i._raw?.isCurrent === true || i._raw?.common?.isCurrent === true);
                if (explicit.length) return explicit;
                const maxV = arr.reduce((mx, d) => {
                  const v = typeof d?.version === 'number' ? d.version : parseInt(d?.version, 10);
                  return Number.isNaN(v) ? mx : Math.max(mx, v);
                }, Number.NEGATIVE_INFINITY);
                return arr.filter(d => {
                  const v = typeof d?.version === 'number' ? d.version : parseInt(d?.version, 10);
                  if (maxV === Number.NEGATIVE_INFINITY) return true;
                  return v === maxV;
                });
              };
              // Build one row per category (single types) or per certificate/title
              // (multiple-instance types). `items` on each unit is that row's current files.
              const rowUnits = [];
              for (const cat of categoryKeys) {
                const itemsAll = groups[cat] || [];
                const current = currentItemsFor(itemsAll);
                const isMulti = MULTI_INSTANCE_DOC_TYPES.includes(cat)
                  || current.some(d => MULTI_INSTANCE_DOC_TYPES.includes(d.documentType));
                if (isMulti) {
                  const byTitle = new Map();
                  for (const d of current) {
                    const t = (d.title && String(d.title).trim()) || '(Untitled)';
                    if (!byTitle.has(t)) byTitle.set(t, []);
                    byTitle.get(t).push(d);
                  }
                  Array.from(byTitle.keys()).sort((a, b) => a.localeCompare(b)).forEach((t) => {
                    rowUnits.push({ cat, title: t, items: byTitle.get(t), rowKey: `${cat}::${t}`, isMulti: true });
                  });
                } else {
                  rowUnits.push({ cat, title: null, items: current, rowKey: cat, isMulti: false });
                }
              }

              return (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>Category</TableCell>
                        <TableCell>Files</TableCell>
                        <TableCell>Latest Upload</TableCell>
                        <TableCell>Nearest Expiry</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Ticket</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowUnits.map((unit) => {
                        const { cat, title: subTitle, items, rowKey, isMulti } = unit;
                        const fileCount = items.length;
                        // Highest version among this row's current files (for the version chip).
                        const rowVersion = items.reduce((mx, d) => {
                          const v = (typeof d?.version === 'number') ? d.version : parseInt(d?.version, 10);
                          return Number.isNaN(v) ? mx : Math.max(mx, v);
                        }, 1);
                        const ts = (d) => (
                          d.updatedAt || d.lastUpdatedAt || d.modifiedAt || d.updatedDate || d.uploadDate || d.createdAt || d.createDateTime || 0
                        );
                        const byDateDesc = (a, b) => new Date(ts(b)) - new Date(ts(a));
                        // Prefer isPrimary first, then active, then latest
                        const byPrimary = (a, b) => {
                          const ap = a.isPrimary ? 1 : 0;
                          const bp = b.isPrimary ? 1 : 0;
                          if (bp !== ap) return bp - ap; // primary first
                          const aa = a.active ? 1 : 0;
                          const ba = b.active ? 1 : 0;
                          if (ba !== aa) return ba - aa; // active next
                          return byDateDesc(a, b); // then latest
                        };
                        const primaryItem = items.slice().sort(byPrimary)[0] || null;

                        const latestUpload = items
                          .map(i => i.uploadDate || i.createdAt || i.updatedAt || i.createDateTime)
                          .filter(Boolean)
                          .sort((a, b) => new Date(b) - new Date(a))[0];
                        let nearestExpiry = primaryItem?.expiryDate || null;
                        // Status based on primary item; fallback aggregate for robustness
                        let aggStatus = primaryItem?.status || 'Valid';
                        if (!primaryItem) {
                          for (const i of items) {
                            if (i.status === 'Expired') { aggStatus = 'Expired'; break; }
                            if (i.status === 'Expiring Soon') { aggStatus = 'Expiring Soon'; }
                          }
                          nearestExpiry = items
                            .map(i => i.expiryDate)
                            .filter(Boolean)
                            .sort((a, b) => new Date(a) - new Date(b))[0] || null;
                        }
                        let daysUntilExpiry = nearestExpiry ? calculateDaysUntilExpiry(nearestExpiry) : null;

                        const isPassportCategory = isPassportCategoryLabel(cat);
                        const isExpanded = !!expandedCats[rowKey];
                        // Group-level common metadata from grouped API (merge across items for robustness)
                        const groupCommon = (() => {
                          const merged = {};
                          const prefer = items.filter(it => it?._raw?.common?.isCurrent === true || it.isCurrent === true);
                          const order = prefer.length > 0 ? prefer.concat(items.filter(it => !prefer.includes(it))) : items;
                          for (const it of order) {
                            const a = it?._raw?.common || {};
                            const b = it?._raw || {};
                            const c = it?.metadata?.common || {};
                            for (const src of [a, b, c]) {
                              if (src && typeof src === 'object') {
                                Object.keys(src).forEach((k) => {
                                  if (merged[k] == null && src[k] != null && src[k] !== '') merged[k] = src[k];
                                });
                              }
                            }
                          }
                          return merged;
                        })();
                        // Passport-specific extra info: prefer values from the active document (robust readers)
                        const activePassportItem = isPassportCategory ? primaryItem : null;
                        const passportNumberRaw = readPassportNumber(activePassportItem);
                        const passportExpiryRaw = readPassportExpiry(activePassportItem);
                        // Fallback to group common if OCR-specific readers didn't return values
                        const displayPassportNumber = passportNumberRaw || (groupCommon?.passportNumber || groupCommon?.passport_no || groupCommon?.passportNo || groupCommon?.documentNumber);
                        const displayPassportExpiry = passportExpiryRaw || (groupCommon?.passportExpiry || groupCommon?.passport_expiry || groupCommon?.expiryDate);
                        const passportDays = displayPassportExpiry ? calculateDaysUntilExpiry(displayPassportExpiry) : null;
                        const passportStatus = displayPassportExpiry ? (passportDays < 0 ? 'Expired' : (passportDays <= 180 ? 'Expiring Soon' : 'Valid')) : null;
                        // Generic passport meta commonly present in group common for all categories
                        const commonPassportNumber = groupCommon?.passportNumber
                          || groupCommon?.passport_no
                          || groupCommon?.passportNo
                          || groupCommon?.documentNumber
                          || primaryItem?.documentNumber
                          || undefined;
                        const commonPassportExpiry = groupCommon?.passportExpiry
                          || groupCommon?.passport_expiry
                          || groupCommon?.expiryDate
                          || primaryItem?.expiryDate
                          || undefined;
                        const genericIssueDate = groupCommon?.issueDate || undefined;

                        // For Passport row, override displayed expiry and status using passportExpiry
                        const displayNearestExpiry = (isPassportCategory && displayPassportExpiry) ? displayPassportExpiry : nearestExpiry;
                        const displayDaysUntil = (isPassportCategory && passportDays !== null) ? passportDays : daysUntilExpiry;
                        const displayStatus = (isPassportCategory && passportStatus) ? passportStatus : aggStatus;
                        // Determine if category has details to show (enable expand icon)
                        const catMeta = readCategoryMetadata(cat, groupCommon, primaryItem);
                        const hasAnyDateish = (() => {
                          try {
                            return Object.entries(groupCommon || {}).some(([k, v]) =>
                              v && /issue|expiry/i.test(k)
                            );
                          } catch { return false; }
                        })();
                        const hasDetails = isPassportCategory
                          ? (displayPassportNumber || displayPassportExpiry)
                          : (catMeta.numberValue || catMeta.expiryValue || catMeta.issueValue || hasAnyDateish);

                        try {
                          console.debug('[EmployeeDetail] Category details check', {
                            categoryKey: cat,
                            isPassportCategory,
                            hasDetails,
                            groupCommon,
                            primaryItemSummary: primaryItem ? {
                              fileName: primaryItem.fileName,
                              expiryDate: primaryItem.expiryDate,
                              documentNumber: primaryItem.documentNumber
                            } : null
                          });
                        } catch (e) { /* ignore */ }

                        const displayCatLabel = isMulti ? `${formatDocTypeLabel(cat)} — ${subTitle}` : formatDocTypeLabel(cat);

                        return (
                          <React.Fragment key={`cat-${rowKey}`}>
                          <TableRow key={`agg-${rowKey}`} hover>
                            <TableCell width={48} sx={{ py: 0 }}>
                              {hasDetails ? (
                                <IconButton size="medium" color="primary" onClick={() => toggleCategoryExpand(rowKey)} aria-label="expand row">
                                  {isExpanded ? <RemoveCircleOutline /> : <AddCircleOutline />}
                                </IconButton>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {displayCatLabel}
                              {isMulti && (
                                <Chip label={`v${rowVersion}`} size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                              )}
                            </TableCell>
                            <TableCell>{fileCount}</TableCell>
                            <TableCell>{latestUpload ? formatDate(latestUpload, true) : 'N/A'}</TableCell>
                            <TableCell>
                              {displayNearestExpiry ? (
                                <>
                                  {formatDate(displayNearestExpiry)}
                                  {displayDaysUntil > 0 && displayDaysUntil <= 180 && (
                                    <> <Warning fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} /> {displayDaysUntil} days left</>
                                  )}
                                </>
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={displayStatus}
                                size="small"
                                color={
                                  displayStatus === 'Valid' ? 'success' :
                                  displayStatus === 'Expired' ? 'error' :
                                  displayStatus === 'Expiring Soon' ? 'warning' : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const ticketDoc = items.find(d => d.ticket);
                                if (!ticketDoc?.ticket) return <Typography variant="body2" color="text.secondary">—</Typography>;
                                return (
                                  <Chip
                                    label={ticketDoc.ticket.orgTicketNumber || `#${ticketDoc.ticket.ticketNumber}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                                {isMulti ? (
                                  <Tooltip title="Replace (new version)" arrow>
                                    <IconButton size="small" onClick={() => handleReplaceSub(cat, subTitle)} sx={{ mr: 0.5 }} aria-label="replace">
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Replace All" arrow>
                                    <IconButton size="small" onClick={() => handleReplaceCategoryClick(cat)} sx={{ mr: 0.5 }} aria-label="replace all">
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="View" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewCategory(items, displayCatLabel)}
                                    sx={{ mr: 0.5 }}
                                    aria-label="view"
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download" arrow>
                                  <IconButton size="small" onClick={() => handleDownloadCategory(items, displayCatLabel)} aria-label="download">
                                    <CloudDownload fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                          {hasDetails && (
                            <TableRow key={`details-${rowKey}`}>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ m: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, color: '#637381' }}>
                                      <NavigateNext fontSize="small" sx={{ mr: 0.75 }} />
                                    </Box>
                                    {isPassportCategory ? (
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Passport Number</Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#212B36', fontSize: '0.9375rem' }}>{displayPassportNumber || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Passport Expiry</Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#212B36', fontSize: '0.9375rem' }}>{displayPassportExpiry ? formatDate(displayPassportExpiry) : 'N/A'}</Typography>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    ) : (
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>{catMeta.numberLabel}</Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#212B36', fontSize: '0.9375rem' }}>{catMeta.numberValue || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>{catMeta.expiryLabel}</Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#212B36', fontSize: '0.9375rem' }}>{catMeta.expiryValue ? formatDate(catMeta.expiryValue) : 'N/A'}</Typography>
                                        </Grid>
                                        {catMeta.issueValue && (
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>{catMeta.issueLabel}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#212B36', fontSize: '0.9375rem' }}>{formatDate(catMeta.issueValue)}</Typography>
                                          </Grid>
                                        )}
                                        {catMeta.issuingAuthorityValue && (
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>{catMeta.issuingAuthorityLabel}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#212B36', fontSize: '0.9375rem' }}>{catMeta.issuingAuthorityValue}</Typography>
                                          </Grid>
                                        )}
                                      </Grid>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}
          </TabPanel>

          {/* Employment/Stakeholder Details Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: '1rem', fontWeight: 500, color: '#1a3e72' }}
                  >
                    {isStakeholder ? 'Stakeholder Information' : 'Position Information'}
                  </Typography>
                  <Grid container spacing={2}>
                    {isStakeholder ? (
                      <>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Role</Typography>
                            <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.employeeRole || 'Not specified'}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Ownership Percentage</Typography>
                            <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.ownershipPercentage || 'Not specified'}</Typography>
                          </Box>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Department</Typography>
                            <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.department?.departmentName}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Position</Typography>
                            <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.position?.positionName}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Reports To</Typography>
                            <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.reportsTo || 'Not specified'}</Typography>
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {!isStakeholder && (
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: '1rem', fontWeight: 500, color: '#1a3e72' }}
                  >
                    Employment Status
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Start Date</Typography>
                        <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{formatDate(employee.startDate)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Contract Type</Typography>
                        <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.contractType || 'Not specified'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}> 
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem', mb: 0.5 }}>
                          Status
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={statusLabel} 
                            size="small"
                            color={
                              employee.status === 'ACTIVE' ? 'success' :
                              employee.status === 'DEACTIVATED' ? 'error' :
                              employee.status === 'ABSCONDED' ? 'warning' : 'default'
                            }
                          />
                          {employee.statusInfo && (
                            <Typography
                              variant="body2"
                              sx={{ color: '#637381', fontSize: '0.875rem' }}
                            >
                              – {employee.statusInfo}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Account Access Tab */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 500,
                      color: '#1a3e72' // Darker blue color for better visibility
                    }}
                  >
                    Account Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Username</Typography>
                        <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{employee.username || 'Not set'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Access Level</Typography>
                        <Chip 
                          label={employee.accessLevel || 'No Access'} 
                          size="small"
                          color={
                            employee.accessLevel === 'CUSTOMER_ADMIN' ? 'primary' :
                            employee.accessLevel === 'CUSTOMER_EMPLOYEE' ? 'info' : 'default'
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Last Login</Typography>
                        <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{formatDateTime(employee.lastLogin) || 'Never'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>Account Created</Typography>
                        <Typography variant="body1" sx={{ color: '#212B36', fontSize: '0.9375rem', fontWeight: 500 }}>{formatDateTime(employee.createdAt)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 500,
                      color: '#1a3e72' // Darker blue color for better visibility
                    }}
                  >
                    Account Actions
                  </Typography>
                  <Box mt={2}>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      fullWidth 
                      sx={{ 
                        mb: 2, 
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#1976d2',
                        borderColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          borderColor: '#1565c0'
                        }
                      }}
                    >
                      Reset Password
                    </Button>
                    <Button 
                      variant="outlined" 
                      color={employee.status === 'Active' ? 'error' : 'success'}
                      fullWidth
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '&.MuiButton-outlinedError': {
                          color: '#d32f2f',
                          borderColor: '#d32f2f',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            borderColor: '#c62828'
                          }
                        },
                        '&.MuiButton-outlinedSuccess': {
                          color: '#2e7d32',
                          borderColor: '#2e7d32',
                          '&:hover': {
                            backgroundColor: 'rgba(46, 125, 50, 0.04)',
                            borderColor: '#1b5e20'
                          }
                        }
                      }}
                    >
                      {employee.status === 'Active' ? 'Deactivate Account' : 'Activate Account'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>
      </Container>

      {/* Document Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal" error={!!uploadErrors.category}>
              <InputLabel id="document-category-label">Document Category</InputLabel>
              <Select
                labelId="document-category-label"
                id="document-category"
                value={uploadData.category}
                label="Document Category"
                onChange={handleUploadChange('category')}
              >
                {uploadCategoryOptions.length > 0 ? (
                  uploadCategoryOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    All document types already exist for this employee
                  </MenuItem>
                )}
              </Select>
              {uploadErrors.category && (
                <FormHelperText>{uploadErrors.category}</FormHelperText>
              )}
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1, mb: 2, mt: 1 }}>
              <Button
                variant="contained"
                component="label"
                color="primary"
                startIcon={<AttachFile />}
                disabled={!uploadData.category}
                sx={{ fontWeight: 600 }}
              >
                CHOOSE FILE
                <input hidden id="document-upload" type="file" multiple onChange={handleFileChange} />
              </Button>
              <Button
                variant="outlined"
                component="label"
                color="primary"
                disabled={!uploadData.category}
                sx={{ fontWeight: 600 }}
              >
                ADD MORE
                <input hidden type="file" multiple onChange={handleFileChange} />
              </Button>
              {(uploadData.files && uploadData.files.length > 0) ? (
                <Box>
                  <Typography variant="body2">
                    {uploadData.files[0].name}
                    {uploadData.files.length > 1 ? ` + ${uploadData.files.length - 1} more` : ''}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">{formatFileSize(uploadData.files[0].size)}</Typography>
                </Box>
              ) : (uploadData.file && (
                <Box>
                  <Typography variant="body2">{uploadData.file.name}</Typography>
                  <Typography variant="caption" color="textSecondary">{formatFileSize(uploadData.file.size)}</Typography>
                </Box>
              ))}
              {(uploadData.files && uploadData.files.length > 0) && (
                <Button size="small" variant="text" color="secondary" onClick={handleClearSelectedFiles}>
                  Clear
                </Button>
              )}
            </Box>
            {uploadErrors.file && (
              <FormHelperText error>{uploadErrors.file}</FormHelperText>
            )}

            {/* Expiry date field removed as per requirement */}

            {MULTI_INSTANCE_DOC_TYPES.includes(uploadData.category) && (
              <TextField
                fullWidth
                required
                margin="normal"
                id="document-name"
                label="Document Name"
                placeholder="e.g. Bachelor Degree, Company X Experience Letter"
                value={uploadData.documentName || ''}
                onChange={handleUploadChange('documentName')}
                error={!!uploadErrors.documentName}
                helperText={uploadErrors.documentName || 'A short name to identify this certificate'}
              />
            )}

            <TextField
              fullWidth
              margin="normal"
              id="document-description"
              label="Description (Optional)"
              multiline
              rows={2}
              value={uploadData.description}
              onChange={handleUploadChange('description')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            color="primary"
            disabled={uploading || availableUploadCategories.length === 0}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Document Viewer Modal */}
      <Dialog
        open={documentViewerOpen}
        onClose={() => {
          try {
            if (documentUrl && typeof documentUrl === 'string' && documentUrl.startsWith('blob:')) {
              URL.revokeObjectURL(documentUrl);
            }
          } catch (e) {
            // ignore
          }
          setDocumentViewerOpen(false);
          setDocumentUrl('');
          setDocumentTitle('');
          setDocumentType('');
        }}
        fullWidth
        maxWidth="lg"
        aria-labelledby="document-viewer-dialog-title"
      >
        <DialogTitle id="document-viewer-dialog-title">
          {documentTitle || 'Document Viewer'}
          <IconButton
            aria-label="close"
            onClick={() => {
              try {
                if (documentUrl && typeof documentUrl === 'string' && documentUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(documentUrl);
                }
              } catch (e) {
                // ignore
              }
              setDocumentViewerOpen(false);
              setDocumentUrl('');
              setDocumentTitle('');
              setDocumentType('');
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0, height: '80vh', display: 'flex', flexDirection: 'column' }}>
          {documentType && documentType.toLowerCase().includes('pdf') ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom>
                PDF preview requires authentication. Click below to open securely.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    const token = getAuthToken();
                    if (!token) throw new Error('Authentication required');

                    if (typeof documentUrl === 'string' && (documentUrl.startsWith('blob:') || documentUrl.startsWith('data:'))) {
                      window.open(documentUrl, '_blank', 'noopener,noreferrer');
                      return;
                    }

                    const base = documentUrl.startsWith('http') ? documentUrl : `${API_PATHS.BASE}${documentUrl}`;
                    const hasToken = /[?&]auth_token=/.test(base);
                    const sep = base.includes('?') ? '&' : '?';
                    const finalUrl = hasToken ? base : `${base}${sep}auth_token=${encodeURIComponent(token)}`;
                    window.open(finalUrl, '_blank', 'noopener,noreferrer');
                  } catch (e) {
                    console.error('Open PDF failed', e);
                  }
                }}
              >
                Open PDF
              </Button>
            </Box>
          ) : documentType && (documentType.toLowerCase().includes('jpg') || documentType.toLowerCase().includes('jpeg') || documentType.toLowerCase().includes('png') || documentType.toLowerCase().includes('gif')) ? (
            (typeof documentUrl === 'string' && (documentUrl.startsWith('blob:') || documentUrl.startsWith('data:'))) ? (
              <img
                src={documentUrl}
                alt={documentTitle || 'Document'}
                style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto', display: 'block' }}
              />
            ) : (
              <AuthenticatedImage
                src={documentUrl}
                alt={documentTitle || 'Document'}
                style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto', display: 'block' }}
              />
            )
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom>
                Preview not available. Please download the document to view it.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    const token = getAuthToken();
                    if (!token) throw new Error('Authentication required');

                    if (typeof documentUrl === 'string' && (documentUrl.startsWith('blob:') || documentUrl.startsWith('data:'))) {
                      window.open(documentUrl, '_blank', 'noopener,noreferrer');
                      return;
                    }

                    const base = documentUrl.startsWith('http') ? documentUrl : `${API_PATHS.BASE}${documentUrl}`;
                    const hasToken = /[?&]auth_token=/.test(base);
                    const sep = base.includes('?') ? '&' : '?';
                    const finalUrl = hasToken ? base : `${base}${sep}auth_token=${encodeURIComponent(token)}`;
                    window.open(finalUrl, '_blank', 'noopener,noreferrer');
                  } catch (e) {
                    console.error('Download failed', e);
                  }
                }}
                sx={{ mt: 2 }}
              >
                Download Document
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            try {
              if (documentUrl && typeof documentUrl === 'string' && documentUrl.startsWith('blob:')) {
                URL.revokeObjectURL(documentUrl);
              }
            } catch (e) {
              // ignore
            }
            setDocumentViewerOpen(false);
            setDocumentUrl('');
            setDocumentTitle('');
            setDocumentType('');
          }}>Close</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={async () => {
              try {
                const token = getAuthToken();
                if (!token) throw new Error('Authentication required');

                if (typeof documentUrl === 'string' && (documentUrl.startsWith('blob:') || documentUrl.startsWith('data:'))) {
                  window.open(documentUrl, '_blank', 'noopener,noreferrer');
                  return;
                }

                const base = documentUrl.startsWith('http') ? documentUrl : `${API_PATHS.BASE}${documentUrl}`;
                const hasToken = /[?&]auth_token=/.test(base);
                const sep = base.includes('?') ? '&' : '?';
                const finalUrl = hasToken ? base : `${base}${sep}auth_token=${encodeURIComponent(token)}`;
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
              } catch (e) {
                console.error('Download failed', e);
              }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EmployeeDetail;
